import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; 
import {
  MdShoppingCart,
  MdLocationOn,
  MdArrowBack,
  MdStar,
  MdCheckCircle,
  MdPayment,
  MdAdd,
  MdRemove,
  MdBrightness1,
  MdLocalShipping,
  MdCheck,
  MdClose,
} from "react-icons/md";
import { createOrders } from "../../services/order.service";
import { getBusiness } from "../../services/auth.service";
import { createCustomer, linkGuestCustomer } from "../../services/customer.service";
import { getCheckoutRiskSettings,getRiskSettings } from "../../services/riskSettings.service";
import { AWS_URL } from '../../services/http.service';

import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getFingerprint } from "../../helpers/fingerprint";
import { motion } from 'framer-motion';
import { RiskOtpDialog } from "../../pages/online-store/RiskOtpDialog";
import TextField from "@mui/material/TextField";
import {
  verifyRiskOtp,
  resendRiskOtp,
  sendRiskOtp
} from "../../services/riskOtp.service";
import {
  getUserCart,
  addToUserCart,
  buyNowUser,
  updateUserCartQuantity,
  removeFromUserCart,
  clearUserCart,
  mergeCarts
} from "../../services/cart.service"; 
// Import shipping calculation services
import {
  calculateOrderTotal,
  getShippingConfig,
  calculateUnifiedShipping    // Added this import
} from "../../services/logistic.service";
 import { getBusinessBySlug } from "../../services/shortBusiness.service.js";
import { getStorefrontPath } from "./storefrontPaths";
import { Country, State } from "country-state-city";
import { Formik, Form } from "formik";
import CustomField from "../../components/common/customField";
import * as Yup from "yup";
import {
  getAddresses,
  addAddress,
  updateAddress,
  fetchCityStateByPincode,
  reverseGeocodeCoordinates,
} from "../../services/address.service";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { AccountLoginDialog } from "./accountLogin";
import {
  getBusinessPaymentMethods,
  createRazorpayOrder,
  createCashfreeOrder,
  verifyRazorpayPayment,
  verifyCashfreePayment,
} from "../../services/payment.service";
import { storeSettingsService } from "../../services/storeSettings.service"; 
import { getBusinessTypeDefaultTheme, shouldUseBusinessDefaultTheme } from "./themeTemplates/businessThemeDefaults";
import { isDarkTheme } from "./themeTemplates/ThemeLayouts";
import { getHardcodedRiskLevel, getEffectiveRiskControls } from "./riskPolicy";
import { applyCoupon } from "../../services/coupon.service";
import LanguageChanger from "../../components/LanguageChenger.jsx";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import {
  getTrackingContext,
  syncTracking,
  trackCartQuantityChange,
  trackPageView,
  trackPurchase,
} from "../../components/common/utlity/tracking.js";
import { debounce } from 'lodash';

function AddressFormDialog({
  open,
  onClose,
  onSubmit,
  initialAddress = "",
  isEditing = false,
  theme,
  isDeliveryPincodeSupported,
}) {
  const [states, setStates] = useState([]);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationFetchError, setLocationFetchError] = useState("");
  const defaultCountryCode = "IN";

  useEffect(() => {
    const stateList = State.getStatesOfCountry(defaultCountryCode).map(
      (state) => ({
        label: state.name,
        value: state.isoCode,
      })
    );
    setStates(stateList);
  }, []);

  useEffect(() => {
    trackPageView("Address Form");
  }, []);

  useEffect(() => {
    if (open) {
      setIsDetectingLocation(false);
      setLocationFetchError("");
    }
  }, [open]);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    phone: Yup.string()
      .required("Mobile number is required")
      .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit mobile number"),
    pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^\d{6}$/, "Pincode must be 6 digits"),
    locality: Yup.string().required("Locality is required"),
    addressLine1: Yup.string()
      .required("Address is required")
      .min(10, "Minimum 10 characters"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
  });

  const parseInitialAddress = (addr) => {
    if (!addr) return { name: "", phone: "", addressLine1: "", addressLine2: "", locality: "", city: "", state: "", pincode: "" };
    if (typeof addr === "object") {
      return {
        name: addr.name || "",
        addressLine1: addr.addressLine1 || "",
        addressLine2: addr.addressLine2 || "",
        phone: addr.phone || "",
        locality: addr.locality || "",
        city: addr.city || "",
        state: addr.state || "",
        pincode: addr.postalCode || "",
      };
    }
    return { name: "", phone: "", addressLine1: addr, addressLine2: "", locality: "", city: "", state: "", pincode: "" };
  };

  const initialValues = parseInitialAddress(initialAddress);

  const getCurrentPosition = (options = {}) =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser"));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
        ...options,
      });
    });

  const getMostAccurateCurrentPosition = async () => {
    let bestPosition = null;
    let bestAccuracy = Number.POSITIVE_INFINITY;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const position = await getCurrentPosition({ timeout: 10000 + attempt * 5000 });
        const accuracy = Number(position?.coords?.accuracy) || Number.POSITIVE_INFINITY;
        if (accuracy < bestAccuracy) {
          bestAccuracy = accuracy;
          bestPosition = position;
        }
        if (bestAccuracy <= 80) break;
      } catch (error) {}
    }
    if (!bestPosition) throw new Error("Unable to detect your current location.");
    return bestPosition;
  };

  const getLocationErrorMessage = (error) => {
    if (!error || typeof error.code !== "number") return error?.message || "Unable to detect your current location.";
    if (error.code === 1) return "Location permission was denied. Please allow location access and try again.";
    if (error.code === 2) return "Unable to detect your current location right now.";
    if (error.code === 3) return "Location request timed out. Please try again.";
    return "Unable to detect your current location.";
  };

  const handleUseCurrentLocation = async (setFieldValue) => {
    try {
      setIsDetectingLocation(true);
      setLocationFetchError("");
      const position = await getMostAccurateCurrentPosition();
      const detectedAddress = await reverseGeocodeCoordinates(
        position.coords.latitude,
        position.coords.longitude
      );
      if (!detectedAddress) throw new Error("We could not convert your current location into an address.");
      setFieldValue("addressLine1", detectedAddress.addressLine1 || "", false);
      setFieldValue("locality", detectedAddress.locality || detectedAddress.city || "", false);
      setFieldValue("city", detectedAddress.city || "", false);
      setFieldValue("state", detectedAddress.state || "", false);
      if (!detectedAddress.pincode || detectedAddress.pincode.length !== 6) {
        setFieldValue("pincode", "", false);
        setLocationFetchError("Couldn't detect a valid 6-digit pincode. Please enter it manually.");
        toast.warn("Couldn't detect a valid 6-digit pincode. Please enter it manually.");
      } else {
        setFieldValue("pincode", detectedAddress.pincode, false);
        setLocationFetchError("");
        toast.success("Current location added to the address form.");
      }
    } catch (error) {
      const message = getLocationErrorMessage(error);
      setLocationFetchError(message);
      toast.error(message);
    } finally {
      setIsDetectingLocation(false);
    }
  };

  if (!open) return null;

  const darkMode = (() => {
    const color = theme?.backgroundColor;
    if (!color) return false;
    let hex = color.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: "16px", sm: "24px" },
          margin: { xs: "8px", sm: "32px" }, // Reduced margin on mobile
          width: { xs: "calc(100% - 16px)", sm: "auto" }, // Better width on mobile
          maxWidth: { xs: "none", sm: "500px" },
          overflow: "hidden",
          backgroundColor: darkMode ? "#1e1e2f" : "#ffffff",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          // Ensure dialog doesn't overflow viewport
          maxHeight: { xs: "calc(100vh - 16px)", sm: "calc(100vh - 64px)" },
        },
      }}
    >
      <div
        className={darkMode ? "bg-gray-900 text-white" : "bg-white"}
        style={{
          fontFamily: theme?.fontFamily || "Inter",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <div
          className={`p-4 sm:p-6 border-b flex justify-between items-center ${
            darkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50"
          }`}
        >
          <h2
            className={`text-lg sm:text-xl font-semibold flex items-center ${
              darkMode ? "text-white" : "text-gray-800"
            }`}
            style={{ fontFamily: theme?.fontFamily || "Inter" }}
          >
            <MdLocationOn
              className={`${darkMode ? "text-blue-400" : "text-blue-600"} mr-2 text-xl sm:text-2xl`}
            />
            {isEditing ? t("ADDRESS_FORM.EDIT_ADDRESS") : t("ADDRESS_FORM.ADD_NEW_ADDRESS")}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-full transition-colors duration-200 ${
              darkMode
                ? "text-gray-400 hover:text-white hover:bg-gray-700"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <MdClose className="text-xl sm:text-2xl" />
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values) => {
            if (
              values.pincode?.trim().length === 6 &&
              typeof isDeliveryPincodeSupported === "function" &&
              !isDeliveryPincodeSupported(values.pincode)
            ) {
              toast.error("We do not deliver to this location.");
              return;
            }
            const countryName = "India";
            onSubmit({
              values,
              city: values.city.trim(),
              state: values.state.trim(),
              country: countryName,
              pincode: values.pincode.trim(),
            });
          }}
        >
          {({ errors, touched, isValid, dirty, handleChange, handleBlur, values, setFieldValue }) => {
            const showUnsupportedPincodeMessage =
              values.pincode?.length === 6 &&
              typeof isDeliveryPincodeSupported === "function" &&
              !isDeliveryPincodeSupported(values.pincode);

            return (
              <Form style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                {/* Scrollable content area with proper max-height */}
                <div 
                  className="p-4 sm:p-6 overflow-y-auto" 
                  style={{ 
                    maxHeight: "calc(60vh - 20px)", 
                    flex: "1 1 auto",
                    overflowX: "hidden"
                  }}
                >
                  <div className="space-y-4 sm:space-y-6">
                    {/* Location detection */}
                    <div
                      className={`rounded-xl border p-3 sm:p-4 ${
                        darkMode ? "border-blue-900 bg-blue-900/20" : "border-blue-100 bg-blue-50"
                      }`}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex-1">
                          <p
                            className={`text-sm font-semibold ${darkMode ? "text-blue-300" : "text-blue-900"}`}
                            style={{ fontFamily: theme?.fontFamily || "Inter" }}
                          >
                            Use your current location
                          </p>
                          <p
                            className={`text-xs mt-1 ${darkMode ? "text-blue-400" : "text-blue-700"}`}
                            style={{ fontFamily: theme?.fontFamily || "Inter" }}
                          >
                            {t("ADDRESS_FORM.DETECT_LOCATION_DESC")}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleUseCurrentLocation(setFieldValue)}
                          disabled={isDetectingLocation}
                          className="inline-flex h-10 min-w-[44px] items-center justify-center rounded-xl text-white shadow-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-60 px-4"
                          style={{
                            backgroundColor: theme?.buttonColor || "#2563eb",
                            fontFamily: theme?.fontFamily || "Inter",
                          }}
                        >
                          <MdLocationOn className="text-base sm:text-lg" />
                          <span className="ml-2 text-sm sm:hidden">Use Location</span>
                        </button>
                      </div>
                      {locationFetchError && (
                        <p className="mt-2 sm:mt-3 text-xs text-red-600">{locationFetchError}</p>
                      )}
                    </div>

                    {/* Name & Mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <CustomField
                        type="text"
                        name="name"
                        label={t("ADDRESS_FORM.NAME")}
                        placeholder={t("ADDRESS_FORM.NAME_PLACEHOLDER")}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.name}
                        error={errors.name}
                        touched={touched.name}
                        extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                          darkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300"
                        }`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      />
                      <CustomField
                        type="text"
                        name="phone"
                        label={t("ADDRESS_FORM.MOBILE_NUMBER")}
                        placeholder={t("ADDRESS_FORM.MOBILE_PLACEHOLDER")}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.phone}
                        error={errors.phone}
                        touched={touched.phone}
                        maxLength={10}
                        extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                          darkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300"
                        }`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      />
                    </div>

                    {/* Pincode & Locality */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <CustomField
                        type="text"
                        name="pincode"
                        label={t("ADDRESS_FORM.PINCODE")}
                        placeholder={t("ADDRESS_FORM.PINCODE_PLACEHOLDER")}
                        value={values.pincode}
                        error={errors.pincode}
                        touched={touched.pincode}
                        maxLength={6}
                        handleChange={async (e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setFieldValue("pincode", value);
                          if (value.length === 6) {
                            const location = await fetchCityStateByPincode(value);
                            if (location) {
                              setFieldValue("city", location.city);
                              setFieldValue("state", location.state);
                            }
                          }
                        }}
                        handleBlur={handleBlur}
                        extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                          darkMode
                            ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300"
                        }`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      />
                      <CustomField
                        type="text"
                        name="locality"
                        label={t("ADDRESS_FORM.LOCALITY")}
                        placeholder={t("ADDRESS_FORM.LOCALITY_PLACEHOLDER")}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.locality}
                        error={errors.locality}
                        touched={touched.locality}
                        extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                          darkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300"
                        }`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      />
                      {showUnsupportedPincodeMessage && (
                        <div className="sm:col-span-2 -mt-2">
                          <p className="text-xs text-red-600">We do not deliver to this location.</p>
                        </div>
                      )}
                    </div>

                    {/* Address Line 1 */}
                    <CustomField
                      type="text"
                      name="addressLine1"
                      label={t("ADDRESS_FORM.ADDRESS_LABEL")}
                      placeholder={t("ADDRESS_FORM.ADDRESS_PLACEHOLDER")}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.addressLine1}
                      error={errors.addressLine1}
                      touched={touched.addressLine1}
                      rows="3"
                      extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                        darkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                          : "bg-white border-gray-300"
                      }`}
                      style={{ fontFamily: theme?.fontFamily || "Inter" }}
                    />

                    {/* Address Line 2 */}
                    <CustomField
                      type="text"
                      name="addressLine2"
                      label={t("ADDRESS_FORM.LANDMARK")}
                      placeholder={t("ADDRESS_FORM.LANDMARK_PLACEHOLDER")}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.addressLine2}
                      error={errors.addressLine2}
                      touched={touched.addressLine2}
                      extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                        darkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                          : "bg-white border-gray-300"
                      }`}
                      style={{ fontFamily: theme?.fontFamily || "Inter" }}
                    />

                    {/* City & State */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <CustomField
                        type="text"
                        name="city"
                        label={t("ADDRESS_FORM.CITY")}
                        placeholder={t("ADDRESS_FORM.CITY_PLACEHOLDER")}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.city}
                        error={errors.city}
                        touched={touched.city}
                        extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                          darkMode
                            ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300"
                        }`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      />
                      <CustomField
                        type="select"
                        name="state"
                        label={t("ADDRESS_FORM.STATE")}
                        placeholder={t("ADDRESS_FORM.SELECT_STATE")}
                        options={states}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        value={values.state}
                        error={errors.state}
                        touched={touched.state}
                        extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                          darkMode
                            ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300"
                        }`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      />
                    </div>

                    {/* Country */}
                    <div className="relative">
                      <input
                        type="text"
                        value="India"
                        disabled
                        className={`w-full p-3 sm:p-4 border rounded-xl cursor-not-allowed ${
                          darkMode
                            ? "bg-gray-800 border-gray-700 text-gray-400"
                            : "bg-gray-100 border-gray-300 text-gray-600"
                        }`}
                        style={{ fontFamily: theme?.fontFamily || "Inter" }}
                      />
                      <MdCheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500 text-lg sm:text-xl" />
                    </div>

                    {/* Alternate Phone */}
                    <CustomField
                      type="text"
                      name="alternatePhone"
                      label={t("ADDRESS_FORM.ALTERNATE_PHONE")}
                      placeholder={t("ADDRESS_FORM.ALTERNATE_PHONE_PLACEHOLDER")}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.alternatePhone}
                      error={errors.alternatePhone}
                      touched={touched.alternatePhone}
                      maxLength={10}
                      extraClass={`w-full p-3 sm:p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                        darkMode
                          ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                          : "bg-white border-gray-300"
                      }`}
                      style={{ fontFamily: theme?.fontFamily || "Inter" }}
                    />
                  </div>
                </div>

                <div
                  className={`p-4 sm:p-6 border-t flex gap-3 sm:gap-4 ${
                    darkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                  }`}
                  style={{ flexShrink: 0 }}
                >
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-3 sm:py-3.5 px-2 border rounded-xl font-medium transition-colors duration-200 text-sm sm:text-base min-h-[48px]"
                    style={{
                      backgroundColor: darkMode ? "#374151" : "#ffffff",
                      borderColor: darkMode ? "#4b5563" : "#d1d5db",
                      color: darkMode ? "#e5e7eb" : "#374151",
                      fontFamily: theme?.fontFamily || "Inter",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode ? "#4b5563" : "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = darkMode ? "#374151" : "#ffffff";
                    }}
                  >
                    {t("ADDRESS_FORM.CANCEL")}
                  </button>
                  <button
                    type="submit"
                    disabled={!isValid || !dirty || showUnsupportedPincodeMessage}
                    className="flex-1 py-3 sm:py-3.5 px-2 text-white rounded-xl font-medium hover:opacity-90 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base min-h-[48px]"
                    style={{
                      backgroundColor: theme?.buttonColor || "#3b82f6",
                      borderRadius:
                        theme?.buttonStyle === "pill"
                          ? "9999px"
                          : theme?.buttonStyle === "rounded"
                          ? "8px"
                          : "4px",
                      fontFamily: theme?.fontFamily || "Inter",
                    }}
                  >
                    {t("ADDRESS_FORM.SAVE_ADDRESS")}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Dialog>
  );
}

// Commented out ShippingMethodDialog as requested
/*
function ShippingMethodDialog({
  open,
  onClose,
  onSelect,
  selectedMethod,
  shippingConfig,
  loading,
  shippingCost,
  deliveryState,
  storeState,
  deliveryCity,
  storeCity,
  orderCalculation,
  orderValue = 0,
  orderWeight = 1,
  theme
}) {
  const [method, setMethod] = useState(selectedMethod || "standard");

  useEffect(() => {
    if (open) {
      if (shippingCost === 0 && shippingConfig?.enableShippingCharges === false) {
        setMethod("pickup");
      }
    }
  }, [open, shippingCost, shippingConfig]);

  const handleConfirm = () => {
    onSelect(method, shippingCost);
    onClose();
  };

  const getEstimatedDays = (method) => {
    const daysMap = {
      "express": "1-2 days",
      "surface": "5-7 days",
      "standard": "3-5 days",
      "logistics_standard": "3-5 days",
      "logistics_express": "1-2 days",
      "pickup": "Ready for pickup",
      "free": "3-5 days"
    };
    return daysMap[method] || "3-5 days";
  };

  const getShippingMethodName = (method) => {
    const names = {
      express: "Express Delivery",
      standard: "Standard Delivery",
      surface: "Economy Delivery",
      free: "Free Shipping",
      pickup: "Store Pickup",
      logistics_standard: "Standard Logistics",
      logistics_express: "Express Logistics",
    };
    return names[method] || method.replace("_", " ").toUpperCase();
  };

  const getShippingMethodIcon = (method) => {
    const icons = {
      express: "⚡",
      standard: "🚚",
      surface: "📦",
      free: "🎁",
      pickup: "🏪",
      logistics_standard: "📊",
      logistics_express: "⚡📊",
    };
    return icons[method] || "📦";
  };

  const getShippingMethods = () => {
    const methods = [];

    const isShippingEnabled = shippingConfig?.enableShippingCharges === true;

    if (isShippingEnabled) {
      const fixedAmount = shippingConfig?.fixedShippingAmount || 0;

      const isSameCity = deliveryCity && storeCity &&
        deliveryCity.trim().toLowerCase() === storeCity.trim().toLowerCase();
      const isSameState = deliveryState && storeState &&
        deliveryState.trim().toLowerCase() === storeState.trim().toLowerCase();

      let shippingAmount = fixedAmount;
      let shippingType = shippingConfig?.defaultShippingMethod || 'standard';
      let reason = `Configured shipping: ₹${fixedAmount}`;

      if (fixedAmount === 0) {
        shippingAmount = 0;
        shippingType = 'free';
        reason = 'Free shipping (configured amount is 0)';
      } else if (shippingConfig?.freeShippingMinOrder && orderValue >= shippingConfig.freeShippingMinOrder) {
        shippingAmount = 0;
        shippingType = 'free';
        reason = `Free shipping (order above ₹${shippingConfig.freeShippingMinOrder})`;
      } else if (shippingConfig?.freeShippingForSameCity && isSameCity) {
        shippingAmount = 0;
        shippingType = 'free';
        reason = 'Free shipping (same city)';
      } else if (shippingConfig?.freeShippingForSameState && isSameState) {
        shippingAmount = 0;
        shippingType = 'free';
        reason = 'Free shipping (same state)';
      }

      methods.push({
        service: shippingType,
        estimated_days: getEstimatedDays(shippingType),
        amount: shippingAmount,
        description: reason,
        source: 'configured',
        isConfigured: true
      });

    } else {
      if (orderCalculation?.shipping) {
        const shippingData = orderCalculation.shipping;
        methods.push({
          service: shippingData.carrier || 'logistics_standard',
          estimated_days: shippingData.estimated_days || '3-5 days',
          amount: shippingData.amount,
          description: shippingData.reason || `Shipping via ${shippingData.carrier || 'logistics'}`,
          carrier_name: shippingData.carrier,
          source: shippingData.source || 'unified',
          isCalculated: true
        });

        if (shippingData.amount > 0) {
          methods.push({
            service: "logistics_express",
            estimated_days: "1-2 days",
            amount: Math.round(shippingData.amount * 1.5),
            description: "Express delivery via logistics",
            carrier_name: shippingData.carrier || 'Logistics Carrier',
            source: "unified_express"
          });
        }
      } else {
        methods.push({
          service: 'logistics_standard',
          estimated_days: '3-5 days',
          amount: shippingCost || 60,
          description: 'Standard logistics delivery',
          carrier_name: 'Logistics Carrier',
          source: 'fallback'
        });
      }
    }

    methods.push({
      service: "pickup",
      estimated_days: "Ready for pickup",
      amount: 0,
      description: "Store pickup",
      source: "pickup"
    });

    return methods;
  };

  const shippingMethods = getShippingMethods();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          minWidth: "450px",
          maxWidth: "500px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          fontFamily: theme?.fontFamily || 'Inter, sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.25rem",
          fontWeight: "600",
          textAlign: "center",
          py: 3,
          borderBottom: "1px solid #eee",
          backgroundColor: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          fontFamily: theme?.fontFamily || 'Inter, sans-serif',
        }}
      >
        <MdLocalShipping className='text-blue-500 text-xl' />
        Shipping Method
      </DialogTitle>

      <DialogContent sx={{ py: 4, fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}>
        <div
          className={`p-3 rounded-lg mb-4 ${shippingConfig?.enableShippingCharges
            ? "bg-green-50 border border-green-200"
            : "bg-blue-50 border border-blue-200"
            }`}
        >
          <div className='flex items-center gap-2'>
            <div
              className={`w-3 h-3 rounded-full ${shippingConfig?.enableShippingCharges ? "bg-green-500" : "bg-blue-500"
                }`}
            ></div>
            <span className='text-sm font-medium'>
              {shippingConfig?.enableShippingCharges
                ? "✅ Shipping charges are ENABLED - Using configured rules"
                : "📊 Shipping charges are DISABLED - Using carrier rates"}
            </span>
          </div>

          {!shippingConfig?.enableShippingCharges && orderCalculation?.shipping && (
            <div className='mt-2 text-xs space-y-1'>
              <div className='text-gray-600'>
                <span className='font-medium'>Source:</span> {orderCalculation.shipping.source}
              </div>
              {orderCalculation.shipping.calculated && (
                <div className='text-green-600'>
                  ✓ Dynamically calculated based on weight and distance
                </div>
              )}
              {orderCalculation.shipping.carrier && (
                <div className='text-blue-600'>
                  Carrier: {orderCalculation.shipping.carrier}
                </div>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className='text-center py-8'>
            <div className='animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto mb-4'></div>
            <Typography variant='body2' sx={{ color: "#64748b", fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}>
              Calculating shipping options...
            </Typography>
          </div>
        ) : shippingMethods.length === 0 ? (
          <div className='text-center py-8'>
            <MdWarning className='text-yellow-500 text-4xl mx-auto mb-4' />
            <Typography variant='body1' sx={{ color: "#92400e", mb: 2, fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}>
              No shipping options available
            </Typography>
            <Typography variant='body2' sx={{ color: "#64748b", fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}>
              Please check your address or contact the store
            </Typography>
          </div>
        ) : (
          <RadioGroup
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            sx={{ mt: 2 }}
          >
            {shippingMethods.map((rate, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "16px",
                  border:
                    method === rate.service
                      ? `2px solid ${theme?.primaryColor || '#3b82f6'}`
                      : "1px solid #e2e8f0",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  marginBottom: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  backgroundColor: method === rate.service ? `${theme?.primaryColor || '#3b82f6'}10` : "#fff",
                }}
                onClick={() => setMethod(rate.service)}
              >
                <Radio
                  value={rate.service}
                  sx={{
                    color: theme?.primaryColor || "#3b82f6",
                    "&.Mui-checked": { color: theme?.primaryColor || "#3b82f6" },
                  }}
                />
                <div style={{ flex: 1, fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span className='text-xl'>
                      {getShippingMethodIcon(rate.service)}
                    </span>
                    <Typography
                      variant='subtitle1'
                      sx={{ fontWeight: 600, color: theme?.headingColor || "#1e293b", fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}
                    >
                      {getShippingMethodName(rate.service)}
                    </Typography>
                    {rate.carrier_name && (
                      <span className='text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full'>
                        {rate.carrier_name}
                      </span>
                    )}
                    {rate.isConfigured && (
                      <span className='text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full'>
                        Configured
                      </span>
                    )}
                  </div>
                  <Typography
                    variant='body2'
                    sx={{ color: theme?.textColor || "#64748b", marginTop: "4px", fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}
                  >
                    {rate.estimated_days} • {rate.description}
                  </Typography>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    minWidth: "80px",
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{ fontWeight: 700, color: theme?.headingColor || "#1e293b", fontFamily: theme?.fontFamily || 'Inter, sans-serif' }}
                  >
                    {rate.amount === 0 ? "FREE" : `₹${rate.amount}`}
                  </Typography>
                </div>
              </div>
            ))}
          </RadioGroup>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 3,
          borderTop: "1px solid #eee",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          variant='outlined'
          sx={{
            px: 3,
            py: 1,
            borderRadius: "8px",
            fontWeight: "600",
            borderWidth: "2px",
            "&:hover": {
              borderWidth: "2px",
              backgroundColor: "rgba(0,0,0,0.05)",
            },
            color: "#64748b",
            borderColor: "#e2e8f0",
            fontFamily: theme?.fontFamily || 'Inter, sans-serif',
          }}
        >
          Cancel
        </Button>
        <Button
          autoFocus
          onClick={handleConfirm}
          variant='contained'
          disabled={loading || shippingMethods.length === 0}
          sx={{
            px: 4,
            py: 1,
            borderRadius: "8px",
            fontWeight: "600",
            backgroundColor: theme?.buttonColor || "#3b82f6",
            "&:hover": {
              backgroundColor: theme?.buttonColor ? `${theme.buttonColor}dd` : "#2563eb",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
            },
            fontFamily: theme?.fontFamily || 'Inter, sans-serif',
          }}
        >
          Select Shipping
        </Button>
      </DialogActions>
    </Dialog>
  );
}
*/

const AddressSelectionDialog = ({
  open,
  onClose,
  addresses,
  onSelect,
  onAddNew,
  theme
}) => {
  const [selectedAddress, setSelectedAddress] = useState(null);

  const defaultAddresses = addresses.filter((addr) => addr.isDefault);

  useEffect(() => {
    if (defaultAddresses.length > 0) {
      setSelectedAddress(defaultAddresses[0]);
    }
  }, [defaultAddresses]);

  const handleConfirm = async () => {
    if (selectedAddress) {
      const didSelect = await onSelect(selectedAddress);
      if (didSelect !== false) {
        onClose();
      }
    }
  };

  const darkMode = theme?.mode === 'dark';

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div 
        className="w-full max-w-md mx-auto rounded-2xl shadow-xl overflow-hidden"
        style={{
          backgroundColor: darkMode ? '#1e1e2f' : '#ffffff',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="p-4 border-b text-center"
          style={{
            borderBottomColor: darkMode ? '#333' : '#e5e7eb',
            backgroundColor: darkMode ? '#2a2a3a' : '#f8fafc'
          }}
        >
          <div className="flex items-center justify-center gap-2">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: darkMode ? '#1e3a8a' : '#dbeafe' }}
            >
              <MdLocationOn style={{ color: darkMode ? '#60a5fa' : '#2563eb', fontSize: 18 }} />
            </div>
            <h2 className="text-xl font-semibold" style={{ color: darkMode ? '#fff' : '#1f2937' }}>
              Delivery Address
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {defaultAddresses.length > 0 ? (
            defaultAddresses.map((address) => {
              const isSelected = selectedAddress?._id === address._id;
              return (
                <div
                  key={address._id}
                  className="p-4 rounded-xl cursor-pointer transition-all"
                  style={{
                    border: isSelected 
                      ? `2px solid ${theme?.primaryColor || '#3b82f6'}`
                      : `1px solid ${darkMode ? '#444' : '#e5e7eb'}`,
                    backgroundColor: isSelected
                      ? (darkMode ? '#1e293b' : '#eff6ff')
                      : (darkMode ? '#1e1e2f' : '#ffffff'),
                  }}
                  onClick={() => setSelectedAddress(address)}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5"
                      style={{
                        borderColor: isSelected 
                          ? (theme?.primaryColor || '#3b82f6')
                          : (darkMode ? '#666' : '#cbd5e1'),
                        backgroundColor: isSelected 
                          ? (theme?.primaryColor || '#3b82f6')
                          : 'transparent',
                      }}
                    >
                      {isSelected && (
                        <MdCheck className="w-full h-full text-white p-0.5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-semibold" style={{ color: darkMode ? '#fff' : '#111827' }}>
                          {address.name || "Default Address"}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          <MdStar size={12} />
                          Default
                        </span>
                      </div>
                      <p className="text-sm mb-1" style={{ color: darkMode ? '#ddd' : '#374151' }}>
                        {address.addressLine1}
                      </p>
                      <p className="text-sm" style={{ color: darkMode ? '#aaa' : '#6b7280' }}>
                        {address.city}, {address.state}, {address.country} - {address.postalCode}
                      </p>
                      {address.landmark && (
                        <p className="text-xs mt-1" style={{ color: darkMode ? '#999' : '#9ca3af' }}>
                          Landmark: {address.landmark}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: darkMode ? '#2a2a3a' : '#f3f4f6' }}
              >
                <MdLocationOn style={{ fontSize: 28, color: darkMode ? '#666' : '#9ca3af' }} />
              </div>
              <h3 className="font-semibold mb-2" style={{ color: darkMode ? '#fff' : '#111827' }}>
                No Default Address Found
              </h3>
              <p className="text-sm" style={{ color: darkMode ? '#aaa' : '#6b7280' }}>
                You haven't set any default address yet. Please add a new address to continue.
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t space-y-2" style={{ borderTopColor: darkMode ? '#333' : '#e5e7eb' }}>
          <button
            onClick={onAddNew}
            className="w-full py-2.5 px-4 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
            style={{
              border: `1px solid ${darkMode ? '#555' : '#e2e8f0'}`,
              backgroundColor: darkMode ? '#1e1e2f' : '#ffffff',
              color: darkMode ? '#ccc' : '#475569',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? '#2a2a3a' : '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? '#1e1e2f' : '#ffffff';
            }}
          >
            <MdAdd size={18} />
            Add Address
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedAddress}
            className="w-full py-2.5 px-4 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: theme?.buttonColor || '#3b82f6',
            }}
            onMouseEnter={(e) => {
              if (!selectedAddress) return;
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <MdCheck size={18} />
              Select Address
            </div>
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 px-4 rounded-xl font-medium transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: darkMode ? '#aaa' : '#64748b',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};


function DeliveryMethodDialog({ open, onClose, onSelect, selectedMethod, theme }) {
  const [method, setMethod] = useState(selectedMethod || "home_delivery");

  const handleConfirm = () => {
    onSelect(method);
    onClose();
  };

  const darkMode = theme?.mode === 'dark';
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          width: "100%",
          maxWidth: {
            xs: "calc(100% - 32px)",
            sm: "400px"
          },
          m: {
            xs: 2,
            sm: "auto"
          },
          overflow: "hidden",
          bgcolor: darkMode ? '#1e1e2f' : '#ffffff',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          textAlign: "center",
          py: 2.5,
          borderBottom: `1px solid ${darkMode ? '#333' : '#e5e7eb'}`,
          bgcolor: darkMode ? '#2a2a3a' : '#f8fafc',
          color: darkMode ? '#fff' : undefined,
        }}
      >
        Delivery Method
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        <RadioGroup
          value={method}
          onChange={(e) => setMethod(e.target.value)}
        >
          {/* Home Delivery */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 12px",
              marginBottom: "12px",
              borderRadius: "12px",
              border: method === "home_delivery"
                ? `2px solid ${theme?.primaryColor || '#3b82f6'}`
                : `1px solid ${darkMode ? '#444' : '#e5e7eb'}`,
              backgroundColor: method === "home_delivery"
                ? (darkMode ? '#1e293b' : '#eff6ff')
                : (darkMode ? '#1e1e2f' : '#fff'),
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <Radio
              value="home_delivery"
              checked={method === "home_delivery"}
              sx={{
                color: theme?.primaryColor || "#3b82f6",
                '&.Mui-checked': {
                  color: theme?.primaryColor || "#3b82f6",
                },
              }}
            />
            <div style={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Home Delivery
              </Typography>
              <Typography variant="body2" sx={{ color: darkMode ? '#aaa' : '#666' }}>
                Get your order delivered to your address
              </Typography>
            </div>
            <div
              style={{
                width: 40,
                height: 40,
                backgroundColor: "#dbeafe",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme?.primaryColor || "#3b82f6",
              }}
            >
              <MdLocalShipping size={20} />
            </div>
          </label>

          {/* Store Pickup */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 12px",
              borderRadius: "12px",
              border: method === "pickup"
                ? `2px solid ${theme?.primaryColor || '#3b82f6'}`
                : `1px solid ${darkMode ? '#444' : '#e5e7eb'}`,
              backgroundColor: method === "pickup"
                ? (darkMode ? '#1e293b' : '#eff6ff')
                : (darkMode ? '#1e1e2f' : '#fff'),
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <Radio
              value="pickup"
              checked={method === "pickup"}
              sx={{
                color: theme?.primaryColor || "#3b82f6",
                '&.Mui-checked': {
                  color: theme?.primaryColor || "#3b82f6",
                },
              }}
            />
            <div style={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Store Pickup
              </Typography>
              <Typography variant="body2" sx={{ color: darkMode ? '#aaa' : '#666' }}>
                Pick up your order from the store
              </Typography>
            </div>
            <div
              style={{
                width: 40,
                height: 40,
                backgroundColor: "#fef3c7",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#f59e0b",
              }}
            >
              <MdShoppingCart size={20} />
            </div>
          </label>
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: `1px solid ${darkMode ? '#333' : '#e5e7eb'}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            py: 1,
            borderRadius: "10px",
            fontWeight: 600,
            borderColor: darkMode ? '#555' : '#e2e8f0',
            color: darkMode ? '#ccc' : '#64748b',
            '&:hover': {
              borderColor: darkMode ? '#777' : '#cbd5e1',
              backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          fullWidth
          sx={{
            py: 1,
            borderRadius: "10px",
            fontWeight: 600,
            bgcolor: theme?.buttonColor || "#3b82f6",
            '&:hover': {
              bgcolor: theme?.buttonColor ? `${theme.buttonColor}dd` : "#2563eb",
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PaymentMethodDialog({
  open,
  total,
  shippingCost,
  taxAmount,
  grandTotal,
  couponDiscount,
  appliedCoupon,
  onClose,
  onPay,
  deliveryMethod,
  address,
  businessUuid,
  theme,
  deliverySlotEnabled,
  preferredDeliveryDate,
  preferredDeliveryTime,
  onPreferredDeliveryDateChange,
  onPreferredDeliveryTimeChange,
  cashOnDeliveryEnabled = true,
}) {
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [availableGateways, setAvailableGateways] = useState({
    razorpay: false,
    cashfree: false,
  });
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);

  const accentColor = theme?.primaryColor || "#2563eb";
  const darkMode = theme?.mode === "dark";

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open && businessUuid) {
      loadPaymentGateways();
    }
  }, [open, businessUuid]);

  const loadPaymentGateways = async () => {
    try {
      const response = await getBusinessPaymentMethods(businessUuid);
      if (response.success) {
        setAvailableGateways({
          razorpay: response.data.razorpay?.isVerified || false,
          cashfree: response.data.cashfree?.isVerified || false,
        });
      }
    } catch (error) {
      console.error("Error loading payment gateways:", error);
    }
  };

  const hasOnlinePayment = availableGateways.razorpay || availableGateways.cashfree;
  const hasCashOnDelivery = cashOnDeliveryEnabled === true;
  const hasAnyPaymentMethod = hasCashOnDelivery || hasOnlinePayment;
  const todayDate = new Date().toISOString().split("T")[0];
  const isHomeDelivery = deliveryMethod === "home_delivery";
  const deliverySlotIncomplete =
    isHomeDelivery &&
    deliverySlotEnabled &&
    ((preferredDeliveryDate && !preferredDeliveryTime) ||
      (!preferredDeliveryDate && preferredDeliveryTime));

  const paymentOptions = [
    hasCashOnDelivery && {
      value: "cod",
      title: "Cash on Delivery",
      subtitle: `Pay ₹${grandTotal} when your order arrives`,
      badge: "Flexible",
      icon: "💰",
      bgColor: "#dbeafe",
      iconColor: accentColor,
    },
    availableGateways.razorpay && {
      value: "razorpay",
      title: "Razorpay",
      subtitle: `Pay ₹${grandTotal} securely online`,
      badge: "Instant",
      icon: "💳",
      bgColor: "#fef3c7",
      iconColor: "#d97706",
    },
    availableGateways.cashfree && {
      value: "cashfree",
      title: "Cashfree",
      subtitle: `Pay ₹${grandTotal} securely online`,
      badge: "Secure",
      icon: "💳",
      bgColor: "#dcfce7",
      iconColor: "#15803d",
    },
  ].filter(Boolean);

  useEffect(() => {
    if (!open) return;
    if (hasCashOnDelivery) {
      setPaymentMethod("cod");
    } else if (availableGateways.razorpay) {
      setPaymentMethod("razorpay");
    } else if (availableGateways.cashfree) {
      setPaymentMethod("cashfree");
    } else {
      setPaymentMethod("");
    }
  }, [open, hasCashOnDelivery, availableGateways.razorpay, availableGateways.cashfree]);

  // Helper function to get responsive button text
  const getButtonText = () => {
    const isMobile = windowWidth < 640;
    
    if (paymentMethod === "cod") {
      if (isMobile) {
        return `Pay ₹${grandTotal}`;
      }
      return `Pay ₹${grandTotal} on Delivery`;
    }
    
    if (isMobile) {
      return `Pay ₹${grandTotal}`;
    }
    return `Pay ₹${grandTotal}`;
  };

  // Helper function to get responsive amount display
  const getAmountDisplay = () => {
    const isMobile = windowWidth < 640;
    if (isMobile) {
      return `₹${grandTotal}`;
    }
    return `₹${grandTotal}`;
  };

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div 
        className="w-full max-w-lg mx-auto rounded-2xl shadow-xl overflow-hidden"
        style={{
          backgroundColor: darkMode ? '#1e1e2f' : '#ffffff',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="p-4 border-b"
          style={{
            borderBottomColor: darkMode ? '#333' : '#e5e7eb',
            backgroundColor: darkMode ? '#2a2a3a' : '#ffffff'
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#dbeafe' }}
              >
                <MdPayment className="text-blue-600 text-xl" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: accentColor }}>
                  Secure Checkout
                </p>
                <h2 className="text-lg sm:text-xl font-bold" style={{ color: darkMode ? '#fff' : '#1f2937' }}>
                  Payment Method
                </h2>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs text-gray-500">Payable now</p>
              <p className="text-xl sm:text-2xl font-bold" style={{ color: accentColor }}>
                {getAmountDisplay()}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Order Summary Card */}
          <div 
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #1e3a8a 100%)',
            }}
          >
            <h3 className="text-xs sm:text-sm font-semibold text-white/80 mb-3 uppercase tracking-wider">
              Order Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-white text-sm sm:text-base">
                <span>Subtotal:</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white/80 text-sm sm:text-base">
                <span>Shipping:</span>
                {shippingCost === 0 ? (
                  <span className="text-green-300">Free</span>
                ) : (
                  <span>₹{shippingCost}</span>
                )}
              </div>
              <div className="flex justify-between text-white/80 text-sm sm:text-base">
                <span>Tax:</span>
                <span>₹{taxAmount.toFixed(2)}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between text-green-300 text-sm sm:text-base">
                  <span>Discount ({appliedCoupon?.name}):</span>
                  <span>-₹{couponDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-white/20 pt-2 mt-2">
                <div className="flex justify-between text-white font-bold text-base sm:text-lg">
                  <span>{t("CART_MODEL.TOTAL")}:</span>
                  <span>₹{grandTotal}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Info Cards */}
          <div className="grid grid-cols-1 gap-2">
            <div 
              className="p-3 rounded-xl flex items-start gap-3"
              style={{
                backgroundColor: darkMode ? '#2a2a3a' : '#f8fafc',
                border: `1px solid ${darkMode ? '#444' : '#e5e7eb'}`
              }}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100 flex-shrink-0">
                <MdLocalShipping className="text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Delivery Method</p>
                <p className="text-sm sm:text-base font-medium" style={{ color: darkMode ? '#fff' : '#1f2937' }}>
                  {isHomeDelivery ? `Home Delivery${shippingCost > 0 ? ` (₹${shippingCost})` : ' (Free)'}` : 'Store Pickup (Free)'}
                </p>
              </div>
            </div>

            {isHomeDelivery && address && (
              <div 
                className="p-3 rounded-xl flex items-start gap-3"
                style={{
                  backgroundColor: darkMode ? '#2a2a3a' : '#f8fafc',
                  border: `1px solid ${darkMode ? '#444' : '#e5e7eb'}`
                }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-100 flex-shrink-0">
                  <MdLocationOn className="text-indigo-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Delivery Address</p>
                  <p className="text-xs sm:text-sm" style={{ color: darkMode ? '#ccc' : '#4b5563' }}>{address}</p>
                </div>
              </div>
            )}
          </div>

          {/* Delivery Slot (Optional) */}
          {isHomeDelivery && deliverySlotEnabled && (
            <div 
              className="p-4 rounded-xl"
              style={{
                backgroundColor: darkMode ? '#422006' : '#fffbeb',
                border: `1px solid ${darkMode ? '#78350f' : '#fde68a'}`
              }}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-sm sm:text-base font-semibold" style={{ color: darkMode ? '#fde68a' : '#92400e' }}>
                    Preferred Delivery Slot
                  </h4>
                  <p className="text-xs mt-1" style={{ color: darkMode ? '#d97706' : '#b45309' }}>
                    Optional - Help us plan delivery better
                  </p>
                </div>
                <span 
                  className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                  style={{ backgroundColor: darkMode ? '#78350f' : '#fef3c7', color: '#b45309' }}
                >
                  Optional
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: darkMode ? '#fde68a' : '#92400e' }}>
                    Date
                  </label>
                  <input
                    type="date"
                    min={todayDate}
                    value={preferredDeliveryDate}
                    onChange={(e) => onPreferredDeliveryDateChange?.(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{
                      backgroundColor: darkMode ? '#1e1e2f' : '#ffffff',
                      borderColor: darkMode ? '#78350f' : '#fde68a',
                      color: darkMode ? '#fff' : '#1f2937'
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: darkMode ? '#fde68a' : '#92400e' }}>
                    Time
                  </label>
                  <input
                    type="time"
                    value={preferredDeliveryTime}
                    onChange={(e) => onPreferredDeliveryTimeChange?.(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{
                      backgroundColor: darkMode ? '#1e1e2f' : '#ffffff',
                      borderColor: darkMode ? '#78350f' : '#fde68a',
                      color: darkMode ? '#fff' : '#1f2937'
                    }}
                  />
                </div>
              </div>
              {deliverySlotIncomplete && (
                <p className="text-xs text-red-500 mt-2">
                  Please select both date and time, or leave both empty
                </p>
              )}
            </div>
          )}

          {/* Payment Options */}
          <div>
            <h3 className="font-bold mb-3 text-sm sm:text-base" style={{ color: darkMode ? '#fff' : '#1f2937' }}>
              Choose Payment Method
            </h3>
            <div className="space-y-3">
              {paymentOptions.map((option) => {
                const isSelected = paymentMethod === option.value;
                return (
                  <div
                    key={option.value}
                    className="p-3 sm:p-4 rounded-xl cursor-pointer transition-all"
                    style={{
                      border: isSelected 
                        ? `2px solid ${accentColor}`
                        : `1px solid ${darkMode ? '#444' : '#e5e7eb'}`,
                      backgroundColor: isSelected
                        ? (darkMode ? '#1e293b' : '#eff6ff')
                        : (darkMode ? '#1e1e2f' : '#ffffff'),
                    }}
                    onClick={() => setPaymentMethod(option.value)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-5 h-5 rounded-full border-2 flex-shrink-0"
                        style={{
                          borderColor: isSelected ? accentColor : (darkMode ? '#666' : '#cbd5e1'),
                          backgroundColor: isSelected ? accentColor : 'transparent',
                        }}
                      >
                        {isSelected && <MdCheck className="w-full h-full text-white p-0.5" />}
                      </div>
                      <div 
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: option.bgColor }}
                      >
                        <span className="text-lg sm:text-xl">{option.icon}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-sm sm:text-base" style={{ color: darkMode ? '#fff' : '#1f2937' }}>
                            {option.title}
                          </h4>
                          <span 
                            className="px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{
                              backgroundColor: isSelected ? accentColor : '#e5e7eb',
                              color: isSelected ? '#fff' : '#6b7280'
                            }}
                          >
                            {option.badge}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm mt-1" style={{ color: darkMode ? '#aaa' : '#6b7280' }}>
                          {option.subtitle}
                        </p>
                      </div>
                      {isSelected && (
                        <div 
                          className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: accentColor }}
                        >
                          <MdCheck className="text-white text-xs sm:text-sm" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {!hasAnyPaymentMethod && (
                <div 
                  className="p-4 rounded-xl text-center"
                  style={{
                    backgroundColor: darkMode ? '#450a0a' : '#fef2f2',
                    border: `1px solid ${darkMode ? '#7f1d1d' : '#fecaca'}`
                  }}
                >
                  <p className="text-sm font-semibold" style={{ color: darkMode ? '#fca5a5' : '#dc2626' }}>
                    No payment methods available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div 
          className="p-4 border-t flex flex-col sm:flex-row gap-3"
          style={{
            borderTopColor: darkMode ? '#333' : '#e5e7eb',
            backgroundColor: darkMode ? '#1e1e2f' : '#ffffff'
          }}
        >
          <button
            onClick={onClose}
            className="order-2 sm:order-1 py-2.5 px-4 rounded-xl font-semibold transition-colors"
            style={{
              border: `1px solid ${darkMode ? '#555' : '#e2e8f0'}`,
              backgroundColor: 'transparent',
              color: darkMode ? '#ccc' : '#64748b',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onPay(paymentMethod, { preferredDeliveryDate, preferredDeliveryTime })}
            disabled={deliverySlotIncomplete || !paymentMethod || !hasAnyPaymentMethod}
            className="order-1 sm:order-2 py-2.5 px-4 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: accentColor,
            }}
            onMouseEnter={(e) => {
              if (!deliverySlotIncomplete && paymentMethod && hasAnyPaymentMethod) {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <span className="text-sm sm:text-base">
              {getButtonText()}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderConfirmationDialog({
  open,
  onClose,
  orderId,
  shippingCost,
  taxAmount,
  grandTotal,
  theme,
}) {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (open) {
      setCountdown(5);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [open, onClose]);

  const darkMode = theme?.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: "16px", sm: "20px" },
          margin: { xs: "16px", sm: "32px" },
          width: { xs: "calc(100% - 32px)", sm: "auto" },
          maxWidth: { xs: "none", sm: "450px" },
          overflow: "hidden",
          boxShadow: darkMode ? "0 10px 30px rgba(0,0,0,0.32)" : "0 10px 30px rgba(0,0,0,0.1)",
          backgroundColor: darkMode ? "#18181b" : "#fff",
        },
      }}
    >
      <DialogContent
        sx={{
          py: { xs: 4, sm: 6 },
          px: { xs: 3, sm: 4 },
          textAlign: "center",
          overflow: "hidden",
          backgroundColor: darkMode ? "#18181b" : undefined,
          color: darkMode ? "#fff" : undefined,
        }}
      >
        {/* Party Popper Animation */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `confetti ${2 + Math.random() * 3}s ease-out forwards`,
                animationDelay: `${Math.random() * 0.5}s`,
              }}
            >
              <div
                className="w-3 h-3 rotate-45"
                style={{
                  background: `hsl(${Math.random() * 360}, 80%, 60%)`,
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              />
            </div>
          ))}
        </div>

        <style>{`
          @keyframes confetti {
            0% {
              transform: translateY(0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(-100px) rotate(720deg);
              opacity: 0;
            }
          }
          .animate-confetti {
            animation: confetti 3s ease-out forwards;
          }
        `}</style>

        <div className="flex flex-col items-center justify-center relative z-10">
          {/* Success Icon with Pulse Animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="relative mb-4 sm:mb-6"
          >
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              <MdCheckCircle className="text-white text-4xl sm:text-5xl" />
            </div>

            {/* Orbiting sparkles */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0"
            >
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                  style={{
                    top: "50%",
                    left: "50%",
                    transform: `rotate(${i * 90}deg) translateY(-38px)`,
                  }}
                />
              ))}
            </motion.div>
          </motion.div>

          {/* Order Confirmed Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Order Confirmed! 🎉
            </Typography>
          </motion.div>

          {/* Order ID Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="mb-3 sm:mb-4"
          >
            <div className="bg-gradient-to-r from-amber-100 to-amber-200 px-4 py-1.5 rounded-full shadow-md">
              <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                Order <span className="font-bold text-amber-800">#{orderId}</span>
              </Typography>
            </div>
          </motion.div>

          {/* Thank You Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-1 mb-4 sm:mb-6"
          >
            <Typography
              variant="body1"
              sx={{
                color: theme?.textColor || "#475569",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Thank you for your purchase! 🛍️
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#94a3b8",
                mt: 0.5,
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              We'll notify you once it ships
            </Typography>
          </motion.div>

          {/* Decorative Element */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "50px" }}
            transition={{ delay: 0.9, duration: 0.5 }}
            className="h-0.5 bg-gradient-to-r from-transparent via-green-400 to-transparent rounded-full my-3 sm:my-4"
          />

          {/* Countdown */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="relative"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" />
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                Closing in {countdown}s
              </Typography>
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" />
            </div>
          </motion.div>

          {/* Decorative circles */}
          <div className="absolute -bottom-10 -left-10 w-16 h-16 bg-green-100 rounded-full opacity-20" />
          <div className="absolute -top-10 -right-10 w-16 h-16 bg-blue-100 rounded-full opacity-20" />
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          pb: { xs: 3, sm: 4 },
          px: { xs: 3, sm: 4 },
          backgroundColor: "#f0fdf4",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth={window.innerWidth < 640}
          sx={{
            px: 4,
            py: 1,
            borderRadius: "8px",
            fontWeight: 600,
            backgroundColor: "#16a34a",
            "&:hover": { backgroundColor: "#15803d" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Close Now
        </Button>
      </DialogActions>
    </Dialog>
  );
}
// OTP Verification Dialog Component
function OTPVerificationDialog({ open, onClose, onVerify, phone, theme }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const sendOTP = async () => {
    setLoading(true);
    try {
      // Call your OTP sending API
      const response = await fetch('/api/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });
      
      if (response.ok) {
        toast.success('OTP sent successfully!');
        setResendDisabled(true);
        setCountdown(30);
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              setResendDisabled(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        toast.error('Failed to send OTP');
      }
    } catch (error) {
      toast.error('Error sending OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      // Call your OTP verification API
      const response = await fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('OTP verified successfully!');
        onVerify(true);
        onClose();
      } else {
        toast.error('Invalid OTP. Please try again.');
        onVerify(false);
      }
    } catch (error) {
      toast.error('Error verifying OTP');
      onVerify(false);
    } finally {
      setLoading(false);
    }
  };

  const darkMode = theme?.mode === 'dark';
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          minWidth: "400px",
          maxWidth: "450px",
          fontFamily: theme?.fontFamily || 'Inter, sans-serif',
          backgroundColor: darkMode ? '#18181b' : '#fff',
        },
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        color: darkMode ? '#fff' : undefined,
        fontFamily: theme?.fontFamily || 'Inter, sans-serif'
      }}>
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-3">
            <span className="text-3xl">🔐</span>
          </div>
          <Typography variant="h6" fontWeight="bold">
            OTP Verification Required
          </Typography>
        </div>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 3, color: darkMode ? '#cbd5e1' : '#64748b' }}>
          For your security, we need to verify your mobile number
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Mobile Number: <strong>{phone}</strong>
        </Typography>
        
        <TextField
          fullWidth
          label="Enter OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          variant="outlined"
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 6 }}
        />
        
        <Button
          fullWidth
          variant="contained"
          onClick={handleVerify}
          disabled={loading || otp.length !== 6}
          sx={{
            mb: 2,
            backgroundColor: theme?.buttonColor || '#3b82f6',
            '&:hover': { backgroundColor: theme?.buttonColor ? `${theme.buttonColor}dd` : '#2563eb' }
          }}
        >
          {loading ? 'Verifying...' : 'Verify OTP'}
        </Button>
        
        <Button
          fullWidth
          variant="text"
          onClick={sendOTP}
          disabled={resendDisabled || loading}
          sx={{ textTransform: 'none' }}
        >
          {resendDisabled ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// Risk Assessment Details Dialog Component
function RiskAssessmentDialog({ open, onClose, assessment, theme }) {
  const darkMode = theme?.mode === 'dark';
  
  if (!assessment || !assessment.show) return null;
  
  const getActionColor = (action) => {
    switch(action) {
      case 'block': return 'text-red-600';
      case 'manual_review': return 'text-orange-600';
      case 'otp_verify': return 'text-yellow-600';
      default: return 'text-blue-600';
    }
  };
  
  const getActionIcon = (action) => {
    switch(action) {
      case 'block': return '🚫';
      case 'manual_review': return '👁️';
      case 'otp_verify': return '🔐';
      default: return '⚠️';
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          minWidth: "450px",
          maxWidth: "500px",
          fontFamily: theme?.fontFamily || 'Inter, sans-serif',
          backgroundColor: darkMode ? '#18181b' : '#fff',
        },
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center',
        borderBottom: darkMode ? '1px solid #27272a' : '1px solid #e2e8f0',
        pb: 2
      }}>
        <div className="flex flex-col items-center">
          <div className={`text-4xl mb-2 ${getActionColor(assessment.action)}`}>
            {getActionIcon(assessment.action)}
          </div>
          <Typography variant="h6" fontWeight="bold">
            Risk Assessment Result
          </Typography>
        </div>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        <div className="space-y-3">
          {/* Risk Score */}
          <div className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: darkMode ? '#1f2937' : '#f3f4f6' }}>
            <span className="font-medium">Risk Score:</span>
            <span className={`font-bold text-lg ${assessment.score > 74 ? 'text-red-600' : assessment.score > 44 ? 'text-orange-600' : 'text-green-600'}`}>
              {assessment.score}/100
            </span>
          </div>
          
          {/* Risk Level */}
          <div className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: darkMode ? '#1f2937' : '#f3f4f6' }}>
            <span className="font-medium">Risk Level:</span>
            <span className={`font-semibold capitalize ${
              assessment.level === 'critical' || assessment.level === 'very_high' ? 'text-red-600' :
              assessment.level === 'high' ? 'text-orange-600' :
              assessment.level === 'medium' ? 'text-yellow-600' : 'text-green-600'
            }`}>
              {assessment.level?.toUpperCase()}
            </span>
          </div>
          
          {/* Recommended Action */}
          <div className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: darkMode ? '#1f2937' : '#f3f4f6' }}>
            <span className="font-medium">Action:</span>
            <span className={`font-semibold capitalize ${getActionColor(assessment.action)}`}>
              {assessment.action?.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          
          {/* Reasons */}
          {assessment.reasons && assessment.reasons.length > 0 && (
            <div className="p-3 rounded-lg" style={{ backgroundColor: darkMode ? '#1f2937' : '#fef3c7' }}>
              <span className="font-medium block mb-2">Risk Factors:</span>
              <ul className="space-y-1">
                {assessment.reasons.map((reason, idx) => (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <span className="text-yellow-600">•</span>
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Dimension Scores */}
          {assessment.dimensionScores && (
            <div className="p-3 rounded-lg" style={{ backgroundColor: darkMode ? '#1f2937' : '#f3f4f6' }}>
              <span className="font-medium block mb-2">Risk Dimensions:</span>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Geographic:</span>
                  <span>{assessment.dimensionScores.geo}/60</span>
                </div>
                <div className="flex justify-between">
                  <span>Customer:</span>
                  <span>{assessment.dimensionScores.customer}/30</span>
                </div>
                <div className="flex justify-between">
                  <span>Transaction:</span>
                  <span>{assessment.dimensionScores.transaction}/20</span>
                </div>
                <div className="flex justify-between">
                  <span>Device:</span>
                  <span>{assessment.dimensionScores.device}/30</span>
                </div>
                <div className="flex justify-between">
                  <span>Payment:</span>
                  <span>{assessment.dimensionScores.payment}/15</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          fullWidth
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: assessment.action === 'block' ? '#dc2626' : (theme?.buttonColor || '#3b82f6'),
            '&:hover': {
              backgroundColor: assessment.action === 'block' ? '#b91c1c' : (theme?.buttonColor ? `${theme.buttonColor}dd` : '#2563eb')
            }
          }}
        >
          {assessment.action === 'block' ? 'Close' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
// Main CartPage Component
function CartPage() {
 const { businessName: urlBusinessName, bussinessUuid } = useParams();
 const isB2BMode = localStorage.getItem('b2b_Price') === 'true' || localStorage.getItem('b2b_intent') === 'true'
  const navigate = useNavigate();
const location = useLocation();

 const [isOtpVerified, setIsOtpVerified] = useState(false);
const [couponCode, setCouponCode] = useState("");
const [appliedCoupon, setAppliedCoupon] = useState(null);
const [couponDiscount, setCouponDiscount] = useState(0);
const [couponLoading, setCouponLoading] = useState(false);
  // Theme state
  const [storeTheme, setStoreTheme] = useState(null);
  const [deliverySlotEnabled, setDeliverySlotEnabled] = useState(false);
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState("");
  const [preferredDeliveryTime, setPreferredDeliveryTime] = useState("");
// Add with your other state declarations
const [otpDialogOpen, setOtpDialogOpen] = useState(false);
const [pendingOTPVerification, setPendingOTPVerification] = useState(null);
const [verificationPhone, setVerificationPhone] = useState('');
  // Shipping calculation states - MODIFIED: Set shipping cost to 0 initially
  const [orderCalculation, setOrderCalculation] = useState(null);
  const [shippingConfig, setShippingConfig] = useState(null);
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
const [captchaError, setCaptchaError] = useState(false);
const [verificationAttempted, setVerificationAttempted] = useState(false);
  // Add these state variables with your other useState declarations
    const [riskOtpDialogOpen, setRiskOtpDialogOpen] = useState(false);
  const [riskOtp, setRiskOtp] = useState("");
  const [riskOtpLoading, setRiskOtpLoading] = useState(false);
  const [riskOtpResendDisabled, setRiskOtpResendDisabled] = useState(false);
  const [riskOtpCountdown, setRiskOtpCountdown] = useState(0);
  const [riskOtpStatus, setRiskOtpStatus] = useState("pending"); // pending, sending, sent, verified, expired
  const [pendingOrderForOtp, setPendingOrderForOtp] = useState(null);
  const [pendingOrderCallback, setPendingOrderCallback] = useState(null);
  const [riskSettings, setRiskSettings] = useState(null);
const [riskSettingsLoading, setRiskSettingsLoading] = useState(true);
useEffect(() => {
  console.log("🔍 riskOtp state changed to:", riskOtp);
}, [riskOtp]);
useEffect(() => {
  const loadRiskSettings = async () => {
    if (!bussinessUuid) return;
    
    try {
      const response = await getCheckoutRiskSettings(bussinessUuid);
  
      if (response?.success && response?.data) {
        setRiskSettings(response.data);
      }
    } catch (error) {
      console.error("Error loading risk settings:", error);
    }
  };
  
  loadRiskSettings();
}, [bussinessUuid]);


const [riskAssessmentDetails, setRiskAssessmentDetails] = useState({
  show: false,
  action: null,
  level: null,
  score: null,
  reasons: [],
  dimensionScores: {},
  flags: {}
});
  // Debouncing refs
  const pendingUpdates = useRef(new Map());
  const updateTimeouts = useRef(new Map());

  useEffect(() => {
    console.log('CartPage URL Parameters:', { urlBusinessName, bussinessUuid });
  }, [urlBusinessName, bussinessUuid]);

  // Cart state with better error handling
  const [cart, setCart] = useState(() => {
    if (!bussinessUuid) {
      console.warn('No business UUID found in URL, cannot load cart');
      return [];
    }
    
    try {
      const savedCart = localStorage.getItem(`cart_${bussinessUuid}`);
      const parsedCart = savedCart ? JSON.parse(savedCart) : [];
      return parsedCart;
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  });
// Helper function to show OTP verification dialog
const showOTPVerificationDialog = (phoneNumber) => {
  return new Promise((resolve) => {
    setOtpDialogOpen(true);
    setPendingOTPVerification(resolve);
    setVerificationPhone(phoneNumber);
  });
};
const [devOtp, setDevOtp] = useState(null);
const [otpSessionId, setOtpSessionId] = useState(null);
const requestRiskOtp = async (phoneNumber) => {
  
  if (!phoneNumber || phoneNumber === "") {
    console.error("❌ Cannot request OTP: No phone number provided");
    toast.error("Phone number is required");
    return false;
  }
  
  setRiskOtpLoading(true);
  setRiskOtpStatus("sending");
  setDevOtp(null);
  
  try {
    const response = await sendRiskOtp(phoneNumber, bussinessUuid);
    
    if (response?.data?.success) {
      setRiskOtpStatus("sent");
      setOtpSessionId(response.data.sessionId);
      
      // Store the dev OTP to display on screen
      if (response.data.devOtp) {
        setDevOtp(response.data.devOtp);
      }
      
      toast.success(response.data.message || "OTP sent successfully!");
      
      // Start countdown for resend
      setRiskOtpResendDisabled(true);
      setRiskOtpCountdown(30);
      const timer = setInterval(() => {
        setRiskOtpCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setRiskOtpResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return true;
    } else {
      toast.error(response?.data?.message || "Failed to generate OTP");
      setRiskOtpStatus("pending");
      return false;
    }
  } catch (error) {
    console.error("Error details:", error?.response?.data);
    toast.error(error?.response?.data?.message || "Failed to send OTP. Please try again.");
    setRiskOtpStatus("pending");
    return false;
  } finally {
    setRiskOtpLoading(false);
  }
};
const resendRiskOtpCode = async () => {
  if (riskOtpResendDisabled) {
    toast.info(`Please wait ${riskOtpCountdown} seconds before requesting again`);
    return false;
  }

  if (!otpSessionId) {
    console.error("❌ No session ID for resend");
    toast.error("Session expired. Please request OTP again.");
    const phoneNumber = verificationPhone || userProfile?.phone || userProfile?.customer_mobile;
    if (phoneNumber) {
      await requestRiskOtp(phoneNumber);
    }
    return false;
  }

  setRiskOtpLoading(true);
  setDevOtp(null);
  
  try {
    const response = await resendRiskOtp(otpSessionId, bussinessUuid);
    
    if (response?.data?.success) {
      if (response.data.devOtp) {
        setDevOtp(response.data.devOtp);
        console.log("🔐 New Dev OTP:", response.data.devOtp);
        toast.info(`🔐 New OTP: ${response.data.devOtp}`, {
          position: "top-center",
          autoClose: 15000,
        });
      }
      
      toast.success(response.data.message || "New OTP sent successfully!");
      
      setRiskOtpResendDisabled(true);
      setRiskOtpCountdown(30);
      const timer = setInterval(() => {
        setRiskOtpCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setRiskOtpResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setRiskOtpStatus("sent");
      return true;
    } else {
      toast.error(response?.data?.message || "Failed to resend OTP");
      return false;
    }
  } catch (error) {
    console.error("Error resending OTP:", error);
    toast.error(error?.response?.data?.message || "Failed to resend OTP");
    return false;
  } finally {
    setRiskOtpLoading(false);
  }
};
const verifyRiskOtpCode = async (otpValue) => {
  const otpToVerify = otpValue || riskOtp;
  

  if (riskOtpStatus === "verified") {
    console.log("⚠️ Already verified, skipping...");
    return true;
  }
  
  if (!otpToVerify || otpToVerify.length !== 6) {
    toast.error("Please enter a valid 6-digit OTP");
    return false;
  }

  if (!otpSessionId) {
    console.error("❌ No OTP session ID found");
    toast.error("Session expired. Please request OTP again.");
    return false;
  }

  if (riskOtpLoading) {
    console.log("⚠️ Verification already in progress, skipping...");
    return false;
  }

  setRiskOtpLoading(true);
  
  try {
    const response = await verifyRiskOtp(otpSessionId, otpToVerify, bussinessUuid);
    
    if (response?.data?.success) {
      setRiskOtpStatus("verified");
      setIsOtpVerified(true);
      toast.success(response.data.message || "OTP verified successfully!");
      
      setRiskOtpDialogOpen(false);
      
      if (pendingOrderCallback) {
        pendingOrderCallback(true);
        setPendingOrderCallback(null);
      }
      
      return true;
    } else {
      const errorMsg = response?.data?.message || "Invalid OTP. Please try again.";
      toast.error(errorMsg);
      return false;
    }
  } catch (error) {
    console.error("❌ Error verifying OTP:", error);
    toast.error(error?.response?.data?.message || "Failed to verify OTP. Please try again.");
    return false;
  } finally {
    setRiskOtpLoading(false);
  }
};

const showOtpVerification = () => {
  console.log("🎯 showOtpVerification called - Opening CAPTCHA dialog");
  
  return new Promise((resolve) => {
    setIsOtpVerified(false);
    
    setRiskOtp("");
    setRiskOtpStatus("pending");
    setOtpSessionId(null);
    setDevOtp(null);
    
    const callback = (verified) => {
      console.log("✅ OTP callback triggered with:", verified);
      if (verified) {
        setIsOtpVerified(true);
      }
      resolve(verified);
      setPendingOrderCallback(null);
    };
    
    setPendingOrderCallback(() => callback);
    
    console.log("🔓 Opening CAPTCHA verification dialog...");
    setRiskOtpDialogOpen(true);
    
    setTimeout(() => {
      console.log("📤 Requesting OTP for CAPTCHA verification");
      requestRiskOtp("captcha_verification");
    }, 500);
  });
};



  // Close OTP dialog
const closeRiskOtpDialog = () => {
  setRiskOtpDialogOpen(false);
  setRiskOtpStatus("pending");
  setRiskOtp("");
  setPendingOrderForOtp(null);
  if (pendingOrderCallback) {
    pendingOrderCallback(false);
    setPendingOrderCallback(null);
  }
};
  // Save cart to localStorage with validation
  useEffect(() => {
    if (bussinessUuid) {
      try {
        localStorage.setItem(`cart_${bussinessUuid}`, JSON.stringify(cart));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    } else {
      console.warn('Cannot save cart: business UUID is undefined');
    }
  }, [cart, bussinessUuid]);

  // Add a fallback for missing business UUID
useEffect(() => {
  if (!bussinessUuid && urlBusinessName) {
    // Try to fetch business UUID from business name
    const fetchBusinessUuid = async () => {
      try {
        const response = await getBusinessBySlug(urlBusinessName);
        if (response.data?.success && response.data?.data?.uuid) {
          // Update URL with correct UUID
          window.history.replaceState(
            {}, 
            '', 
            `/${urlBusinessName}/store/${response.data.data.uuid}/cart`
          );
          // Reload the page to use new URL
          window.location.reload();
        }
      } catch (error) {
        console.error('Error fetching business UUID:', error);
        toast.error('Unable to load cart: Store not found');
      }
    };
    fetchBusinessUuid();
  }
}, [bussinessUuid, urlBusinessName]);
  // User profile state
  const [userProfile, setUserProfile] = useState(() => {
    const savedUser = localStorage.getItem(`user_${bussinessUuid}`);
    const savedGuest = localStorage.getItem(`guest_${bussinessUuid}`);
    return savedUser
      ? JSON.parse(savedUser)
      : savedGuest
        ? JSON.parse(savedGuest)
        : null;
  });

  // Dialog states - MODIFIED: Remove shippingMethodDialogOpen state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [deliveryMethodDialogOpen, setDeliveryMethodDialogOpen] =
    useState(false);
  // const [shippingMethodDialogOpen, setShippingMethodDialogOpen] = useState(false); // Commented out
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressSelectionDialogOpen, setAddressSelectionDialogOpen] =
    useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [orderConfirmationDialogOpen, setOrderConfirmationDialogOpen] =
    useState(false);

  // Delivery and shipping states
  const [deliveryMethod, setDeliveryMethod] = useState("home_delivery");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [deliveryState, setDeliveryState] = useState("");
  const [deliveryPincode, setDeliveryPincode] = useState("");
  const [deliveryAddressConfirmed, setDeliveryAddressConfirmed] = useState(false);

  // Store and order states
  const [store, setStore] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [userAddresses, setUserAddresses] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [checkoutStep, setCheckoutStep] = useState("cart");
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  // Shipping states - MODIFIED: Set shipping cost to 0 and default method
  const [shippingCost, setShippingCost] = useState(0); // Set to 0 initially
  const [shippingMethod, setShippingMethod] = useState("standard"); // Default method
  const [cashOnDeliveryEnabled, setCashOnDeliveryEnabled] = useState(true);
  const [customerRiskLevel, setCustomerRiskLevel] = useState("low");
  const [riskControls, setRiskControls] = useState({
    disableCod: false,
    disableReturns: false,
    blockCheckout: false,
    approvalMethod: "approve",
  });
  const isCodAllowedForCustomer = cashOnDeliveryEnabled && !riskControls.disableCod;

  const normalizePincode = (value = "") => String(value).replace(/\D/g, "").slice(0, 6);
  const formatPreferredDeliverySlot = () => {
    if (preferredDeliveryDate && preferredDeliveryTime) {
      return `${preferredDeliveryDate} at ${preferredDeliveryTime}`;
    }

    return preferredDeliveryDate || preferredDeliveryTime || "";
  };

  const allowedDeliveryPincodes = Array.isArray(shippingConfig?.allowedDeliveryPincodes)
    ? shippingConfig.allowedDeliveryPincodes
        .map((pincode) => normalizePincode(pincode))
        .filter((pincode) => pincode.length === 6)
    : [];

  const isPincodeDeliveryEnabled = shippingConfig?.pincodeDeliveryEnabled === true;

  const isDeliveryPincodeSupported = (pincode, configOverride = shippingConfig) => {
    const normalizedAllowedDeliveryPincodes = Array.isArray(configOverride?.allowedDeliveryPincodes)
      ? configOverride.allowedDeliveryPincodes
          .map((allowedPincode) => normalizePincode(allowedPincode))
          .filter((allowedPincode) => allowedPincode.length === 6)
      : [];
    const isRestrictedByPincode = configOverride?.pincodeDeliveryEnabled === true;
    const normalizedPincode = normalizePincode(pincode);

    if (!isRestrictedByPincode) {
      return true;
    }

    if (!normalizedPincode) {
      return false;
    }

    if (normalizedAllowedDeliveryPincodes.length === 0) {
      return false;
    }

    return normalizedAllowedDeliveryPincodes.includes(normalizedPincode);
  };

  const fetchLatestShippingConfig = async () => {
    if (!bussinessUuid) {
      return shippingConfig;
    }

    try {
      const response = await getShippingConfig(bussinessUuid);
      const latestConfig = response?.data?.data || null;

      if (latestConfig) {
        setShippingConfig(latestConfig);
        setDeliverySlotEnabled(latestConfig?.preferredDeliverySlotEnabled === true);
        return latestConfig;
      }
    } catch (error) {
      console.error("Failed to fetch latest shipping config:", error);
    }

    return shippingConfig;
  };
const handleBackNavigation = () => {
    const stateFrom = location?.state?.from;
    if (stateFrom && stateFrom !== location.pathname) {
      navigate(stateFrom);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(
      getStorefrontPath({
        businessName: displayBusinessName,
        bussinessUuid,
      }),
      { replace: true }
    );
  };
  const validateDeliveryPincode = async (pincode) => {
    const latestConfig = await fetchLatestShippingConfig();
    return isDeliveryPincodeSupported(pincode, latestConfig);
  };

  const ensureDeliveryLocationSupported = (pincode = deliveryPincode) => {
    if (deliveryMethod !== "home_delivery") {
      return true;
    }

    if (!isPincodeDeliveryEnabled) {
      return true;
    }

    if (isDeliveryPincodeSupported(pincode)) {
      return true;
    }

    toast.error("We do not deliver to this location.");
    return false;
  };
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid) || businessData[0]
    : businessData;
  
  // Use business name from URL or from Redux store
  const displayBusinessName = urlBusinessName || business?.businessName || "Store";

  // const resolveUiMode = () => {
  //   if (typeof window === "undefined") return "light";
  //   // const savedTheme = localStorage.getItem("theme");
  //   if (savedTheme === "dark" || savedTheme === "light") {
  //     return savedTheme;
  //   }
  //   if (document.documentElement.classList.contains("dark")) {
  //     return "dark";
  //   }
  //   return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
  //     ? "dark"
  //     : "light";
  // };

  const withUiMode = (themeObj = {}) => ({
    ...themeObj,
    // mode: resolveUiMode(),
  });

  const isDarkMode = storeTheme ? isDarkTheme(storeTheme) : false;

  // Theme styles
  const themeStyles = {
    fontFamily: storeTheme?.fontFamily || 'Inter, system-ui, -apple-system, sans-serif',
    backgroundColor: isDarkMode ? '#0b1220' : (storeTheme?.backgroundColor || '#f9fafb'),
    color: isDarkMode ? '#e5e7eb' : (storeTheme?.textColor || '#1f2937'),
  };

  const headingStyle = {
    color: isDarkMode ? '#f8fafc' : (storeTheme?.headingColor || '#111827'),
    fontFamily: storeTheme?.fontFamily || 'Inter, system-ui, -apple-system, sans-serif',
  };

  const buttonStyle = {
    backgroundColor: storeTheme?.buttonColor || '#3b82f6',
    color: '#ffffff',
    borderRadius: storeTheme?.buttonStyle === 'pill' ? '9999px' : 
                storeTheme?.buttonStyle === 'rounded' ? '8px' : '4px',
    fontFamily: storeTheme?.fontFamily || 'Inter, system-ui, -apple-system, sans-serif',
  };

  const primaryColorStyle = {
    color: storeTheme?.primaryColor || '#3b82f6',
    fontFamily: storeTheme?.fontFamily || 'Inter, system-ui, -apple-system, sans-serif',
  };

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem(`cart_${bussinessUuid}`, JSON.stringify(cart));
  }, [cart, bussinessUuid]);
useEffect(() => {
  trackPageView("cart");
}, []);

  useEffect(() => {
    const computedRiskLevel = getHardcodedRiskLevel({
      customerId: userProfile?.customerId || userProfile?._id,
      phone: userProfile?.phone || userProfile?.customer_mobile,
    });

    setCustomerRiskLevel(computedRiskLevel);
    setRiskControls(
      getEffectiveRiskControls({
        businessUuid: bussinessUuid,
        riskLevel: computedRiskLevel,
      }),
    );
  }, [bussinessUuid, userProfile]);

  // Fetch store data and theme
  useEffect(() => {
    if (!bussinessUuid) return;

    const fetchStoreAndTheme = async () => {
      let businessData = null;
      try {
        const res = await getBusiness(bussinessUuid);
        businessData = res?.data || null;
        if (businessData) {
          setStore(businessData);
        }
      } catch (e) {
        console.error("Error fetching store data:", e);
      }

      try {
        const settingsResponse = await storeSettingsService.getPublicStoreSettings(bussinessUuid);
          const fallbackTheme = getBusinessTypeDefaultTheme(businessData);
        if (settingsResponse.success) {
            const resolvedTheme = shouldUseBusinessDefaultTheme(settingsResponse.data?.theme)
              ? fallbackTheme
              : settingsResponse.data.theme;
            setStoreTheme(withUiMode(resolvedTheme));
            setCashOnDeliveryEnabled(
              settingsResponse.data?.checkoutSettings?.cashOnDeliveryEnabled !== false,
            );
        } else {
            setStoreTheme(withUiMode(fallbackTheme));
            setCashOnDeliveryEnabled(true);
        }
      } catch (error) {
          setStoreTheme(withUiMode(getBusinessTypeDefaultTheme(businessData)));
          setCashOnDeliveryEnabled(true);
      }
    };

    fetchStoreAndTheme();
  }, [bussinessUuid]);

  useEffect(() => {
    if (deliveryMethod !== "home_delivery" || deliverySlotEnabled !== true) {
      setPreferredDeliveryDate("");
      setPreferredDeliveryTime("");
    }
  }, [deliveryMethod, deliverySlotEnabled]);

  // Fetch shipping configuration once on mount or when bussinessUuid changes
  useEffect(() => {
    if (!bussinessUuid) return;
    const fetchShippingConfig = async () => {
      try {
        const res = await getShippingConfig(bussinessUuid);
        const configData = res?.data?.data || res?.data || null;
        if (configData) {
          setShippingConfig(configData);
          setDeliverySlotEnabled(configData?.preferredDeliverySlotEnabled === true);
        }
      } catch (err) {
        console.error("Error fetching shipping config:", err);
      }
    };
    fetchShippingConfig();
  }, [bussinessUuid]);

  // Re-calculate shipping whenever cart subtotal or shippingConfig changes
  useEffect(() => {
    if (
      deliveryMethod !== "home_delivery" ||
      !shippingConfig?.enableShippingCharges ||
      !deliveryAddressConfirmed
    ) {
      setShippingCost(0);
      return;
    }

    const qty = cart.reduce((acc, item) => acc + (item.cartQuantity || 1), 0);
    // Simple subtotal check (without tax) for minimum buy condition
    const currentSubtotal = cart.reduce((acc, item) => {
      const price = isB2BMode ? (item.b2bSalePrice ?? item.salePrice) : item.salePrice;
      const itemQty = item.cartQuantity || 1;
      let basePrice = Number(price || 0) * itemQty;
      let discountAmount = 0;
      if (item.discount) {
        if (item.discountUnit === "%") {
          discountAmount = (basePrice * Number(item.discount.replace(/\r\n/g, ""))) / 100;
        } else {
          discountAmount = Number(item.discount.replace(/\r\n/g, ""));
        }
      }
      return acc + (basePrice - discountAmount);
    }, 0);

    const fixedAmount = shippingConfig.fixedShippingAmount || 0;
    
    // Condition to check if total meets minimum buy order limit
    if (fixedAmount === 0 || (shippingConfig.freeShippingMinOrder && currentSubtotal >= shippingConfig.freeShippingMinOrder)) {
      setShippingCost(0);
    } else {
      setShippingCost(fixedAmount);
    }
    
    setOrderCalculation(null); // Optional since we are taking simple fixed calculation based on limit
  }, [cart, deliveryMethod, shippingConfig, isB2BMode]);

  // Load user addresses
  useEffect(() => {
    if (userProfile && userProfile.addresses) {
      setUserAddresses(userProfile.addresses);
    } else if (userProfile && userProfile.address) {
      setUserAddresses([userProfile.address]);
    }
  }, [userProfile]);

  // Load addresses from API
  useEffect(() => {
    const loadUserAddresses = async () => {
      if (userProfile?._id && bussinessUuid && !userProfile?.isGuest) {
        try {
          const res = await getAddresses(bussinessUuid, userProfile._id);
          if (res.data) {
            const addresses = res.data;
            setUserAddresses(addresses);

            const defaultAddress = addresses.find((addr) => addr.isDefault);
                    // Do not auto-select a default delivery address.
            // The user must explicitly choose the address for tax/shipping calculation.
          }
        } catch (err) {
          console.error("Error loading user addresses:", err);
        }
      }
    };

    loadUserAddresses();
  }, [userProfile, bussinessUuid]);

  // Create debounced update function for logged-in users
  const debouncedUpdateCart = useCallback(
    debounce(async (bussinessUuid, customerId, productId, quantity, variantKey, itemId, productName) => {
      try {
        // Clear the timeout for this item
        if (updateTimeouts.current.has(itemId)) {
          clearTimeout(updateTimeouts.current.get(itemId));
          updateTimeouts.current.delete(itemId);
        }

        // Get the final quantity from pending updates
        const finalQuantity = pendingUpdates.current.get(itemId) || quantity;
        
        // Clear pending update
        pendingUpdates.current.delete(itemId);

        // Make the API call
        await updateUserCartQuantity(
          bussinessUuid,
          customerId,
          productId,
          finalQuantity,
          variantKey
        );

        // Track the final change
        trackCartQuantityChange({
          productId: itemId,
          bussinessUuid,
          action: "UPDATE",
          quantity: finalQuantity,
          productName: productName
        });

      } catch (error) {
        console.error('Error updating cart:', error);
        toast.error('Failed to update cart');
        
        // Revert the local state if API fails
        // You might want to implement a more sophisticated revert logic here
      }
    }, 500), // 500ms debounce delay
    []
  );
const calculateOrderTotals = () => {
  const storeState = store?.state || "";
  const isDeliveryAddressSelected = deliveryMethod !== "home_delivery" || deliveryAddressConfirmed;

  // Calculate taxes for each item
  const updatedCart = cart.map((item) =>
    calculateGSTForItem(item, deliveryState, storeState, isDeliveryAddressSelected)
  );

  const subtotal = Number(
    updatedCart
      .reduce((acc, item) => acc + (item.baseAmount || 0), 0)
      .toFixed(2)
  );

  const taxAmount = Number(
    updatedCart
      .reduce((acc, item) => {
        const itemTax =
          (item.taxBreakup?.cgst || 0) +
          (item.taxBreakup?.sgst || 0) +
          (item.taxBreakup?.igst || 0) +
          (item.taxBreakup?.cess || 0);
        return acc + itemTax;
      }, 0)
      .toFixed(2)
  );

  const safeDiscount = Number(couponDiscount || 0);
  
  // Calculate grand total with discount
  const grandTotal = Number(
    (subtotal + taxAmount + shippingCost - safeDiscount).toFixed(2)
  );

  return { subtotal, taxAmount, grandTotal, discount: safeDiscount, updatedCart };
};
  const getSixDigitHexTimestamp = () => {
    const timestamp = Math.floor(Date.now() / 1000);
    const hex = timestamp.toString(16);
    return hex.slice(-6).padStart(6, "0").toUpperCase();
  };

const calculateGSTForItem = (item, deliveryState, storeState, isDeliveryAddressSelected) => {
  
  const qty = Number(item.cartQuantity || 1);
  const price = isB2BMode
    ? item.b2bSalePrice ?? item.salePrice 
    : item.salePrice;

  let basePrice = Number(price || 0) * qty;
  let discountAmount = 0;

  if (item.discount) {
    if (item.discountUnit === "%") {
      discountAmount = (basePrice * Number(item.discount.replace(/\r\n/g, ""))) / 100;
    } else {
      discountAmount = Number(item.discount.replace(/\r\n/g, ""));
    }
  }

  basePrice = basePrice - discountAmount;

  const cgstRate = Number(item.cgst || 0);
  const sgstRate = Number(item.sgst_utgst || 0);
  const igstRate = Number(item.igst || 0);
  const cessRate = Number(item.cess || 0);

  let cgst = 0, sgst = 0, igst = 0, cess = 0;

  const shouldCalculateTax = deliveryMethod !== "home_delivery" || deliveryAddressConfirmed;
  
  if (item.taxable_v === "taxable" && shouldCalculateTax) {
    // FIXED LOGIC:
    // Case 1: Store Pickup - ALWAYS intra-state (CGST + SGST)
    // Case 2: Home Delivery - Check if delivery state matches store state
    let isIntraState = false;
    
    if (deliveryMethod !== "home_delivery") {
      // Store pickup - always intra-state
      isIntraState = true;
    } else {
      // Home delivery - compare states
      isIntraState = deliveryState && storeState && 
        deliveryState.trim().toLowerCase() === storeState.trim().toLowerCase();
    }

    if (isIntraState) {
      // Intra-state: Use CGST + SGST
      cgst = (basePrice * cgstRate) / 100;
      sgst = (basePrice * sgstRate) / 100;
    } else {
      // Inter-state: Use IGST
      igst = (basePrice * igstRate) / 100;
    }

    // CESS applies regardless (for both intra and inter)
    if (cessRate > 0) {
      cess = (basePrice * cessRate) / 100;
    }
  }

  cgst = Number(cgst.toFixed(2));
  sgst = Number(sgst.toFixed(2));
  igst = Number(igst.toFixed(2));
  cess = Number(cess.toFixed(2));

  const totalWithTax = Number((basePrice + cgst + sgst + igst + cess).toFixed(2));

  return {
    ...item,
    baseAmount: Number(basePrice.toFixed(2)),
    taxBreakup: {
      cgst,
      sgst,
      igst,
      cess,
      cgstRate,
      sgstRate,
      igstRate,
      cessRate,
    },
    totalWithTax,
  };
};



  const handleGuestMode = () => {
    const guestProfile = {
      name: "Guest",
      phone: "",
      address: "",
      isGuest: true,
    };
    setUserProfile(guestProfile);
    setAccountDialogOpen(false);
    localStorage.setItem(
      `guest_${bussinessUuid}`,
      JSON.stringify(guestProfile)
    );
    toast.success("Continuing as guest");
  };

// In CartPage.jsx - Update the loadCart useEffect
useEffect(() => {
  const loadCart = async () => {
    if (!bussinessUuid) return;
    
    try {
      const userProfile = JSON.parse(localStorage.getItem(`user_${bussinessUuid}`) || 'null');
      
      if (userProfile && !userProfile.isGuest) {
       
        const response = await getUserCart(bussinessUuid, userProfile.customerId);
        
        if (response && response.success && response.data && response.data.items) {
          setCart(response.data.items);
        } else {
          console.log("No items in backend cart or invalid response:", response);
          setCart([]);
        }
      } else {
      
        const savedCart = localStorage.getItem(`cart_${bussinessUuid}`);
        const parsedCart = savedCart ? JSON.parse(savedCart) : [];
        setCart(parsedCart);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setCart([]);
    }
  };
  
  loadCart();
}, [bussinessUuid]);

const handleIncrement = async (id) => {
  const userProfile = JSON.parse(localStorage.getItem(`user_${bussinessUuid}`) || 'null');
  
  // Find the item in cart
  const item = cart.find(item => item._id === id);
  if (!item) return;

  // Get the current quantity in cart
  const currentQty = item.cartQuantity || 1;
  
  // Determine the maximum available stock
  // For variant products, use variantStock, otherwise use quantity
  let maxStock = 0;
  
  if (item.hasVariants) {
    // For variant products, use variantStock
    maxStock = item.variantStock !== undefined ? item.variantStock : item.quantity || 0;
  } else {
    // For simple products, use quantity
    maxStock = item.quantity || 0;
  }

  // Check if we've reached the maximum stock
  if (currentQty >= maxStock) {
    toast.warning(`${item.productName} has only ${maxStock} items in stock`);
    return;
  }

  const newQty = currentQty + 1;

  if (userProfile && !userProfile.isGuest) {
    // Update local state immediately for responsive UI
    setCart(prev =>
      prev.map(item =>
        item._id === id
          ? { ...item, cartQuantity: newQty }
          : item
      )
    );

    // Store the pending update
    pendingUpdates.current.set(id, newQty);

    // Clear any existing timeout for this item
    if (updateTimeouts.current.has(id)) {
      clearTimeout(updateTimeouts.current.get(id));
    }

    // Set a new timeout as fallback (in case debounce doesn't trigger)
    const timeoutId = setTimeout(() => {
      if (pendingUpdates.current.has(id)) {
        const finalQty = pendingUpdates.current.get(id);
        debouncedUpdateCart(
          bussinessUuid,
          userProfile.customerId,
          item.productId || item._id,
          finalQty,
          item.variantKey,
          id,
          item.productName
        );
      }
    }, 1000);

    updateTimeouts.current.set(id, timeoutId);

    // Trigger debounced update
    debouncedUpdateCart(
      bussinessUuid,
      userProfile.customerId,
      item.productId || item._id,
      newQty,
      item.variantKey,
      id,
      item.productName
    );

  } else {
    // For guests, update localStorage immediately
    setCart(prev =>
      prev.map((item) => {
        if (item._id === id) {
          if (currentQty >= maxStock) {
            toast.warning(`${item.productName} has only ${maxStock} items in stock`);
            return item;
          }
          
          trackCartQuantityChange({
            productId: item._id,
            bussinessUuid,
            action: "INCREMENT",
            quantity: newQty,
            productName: item.productName
          });
          
          return { ...item, cartQuantity: newQty };
        }
        return item;
      })
    );
  }
};

const handleDecrement = async (id) => {
  const userProfile = JSON.parse(localStorage.getItem(`user_${bussinessUuid}`) || 'null');
  
  // Find the item in cart
  const item = cart.find(item => item._id === id);
  if (!item) return;

  const currentQty = item.cartQuantity || 1;
  
  // Don't allow quantity below 1
  if (currentQty <= 1) {
    return;
  }

  const newQty = currentQty - 1;

  if (userProfile && !userProfile.isGuest) {
    // Update local state immediately for responsive UI
    setCart(prev =>
      prev.map(item =>
        item._id === id
          ? { ...item, cartQuantity: newQty }
          : item
      )
    );

    // Store the pending update
    pendingUpdates.current.set(id, newQty);

    // Clear any existing timeout for this item
    if (updateTimeouts.current.has(id)) {
      clearTimeout(updateTimeouts.current.get(id));
    }

    // Set a new timeout as fallback
    const timeoutId = setTimeout(() => {
      if (pendingUpdates.current.has(id)) {
        const finalQty = pendingUpdates.current.get(id);
        debouncedUpdateCart(
          bussinessUuid,
          userProfile.customerId,
          item.productId || item._id,
          finalQty,
          item.variantKey,
          id,
          item.productName
        );
      }
    }, 1000);

    updateTimeouts.current.set(id, timeoutId);

    // Trigger debounced update
    debouncedUpdateCart(
      bussinessUuid,
      userProfile.customerId,
      item.productId || item._id,
      newQty,
      item.variantKey,
      id,
      item.productName
    );

  } else {
    setCart(prev =>
      prev.map((item) => {
        if (item._id === id && currentQty > 1) {
          trackCartQuantityChange({
            productId: item._id,
            bussinessUuid,
            action: "DECREMENT",
            quantity: newQty,
            productName: item.productName
          });
          
          return { ...item, cartQuantity: newQty };
        }
        return item;
      })
    );
  }
};


const handleRemoveFromCart = async (id) => {
  const userProfile = JSON.parse(localStorage.getItem(`user_${bussinessUuid}`) || 'null');
  const product = cart.find((item) => item._id === id);
  
  if (userProfile && !userProfile.isGuest) {
    try {
      const customerId = userProfile.customerId; // This is "61f94706-834e-4bc8-851a-1272c1379148"
      const productId = product.productId || product._id;
    
      const response = await removeFromUserCart(
        bussinessUuid,
        customerId, // Use customerId, not userProfile._id
        productId,
        product.variantKey
      );
      
      
      if (response && response.success) {
        setCart((prev) => prev.filter((item) => item._id !== id));
        toast.success(`${product.productName} removed from cart`);
      } else {
        toast.error(response?.message || "Failed to remove item");
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove item');
    }
  } else {
    // Guest user - remove from localStorage
    setCart((prev) => prev.filter((item) => item._id !== id));
    localStorage.setItem(`cart_${bussinessUuid}`, JSON.stringify(cart.filter((item) => item._id !== id)));
    if (product) {
      toast.success(`${product.productName} removed from cart`);
    }
  }
};
// Updated emptyCart for logged-in users
const emptyCart = async () => {
  const userProfile = JSON.parse(localStorage.getItem(`user_${bussinessUuid}`) || 'null');
  
  if (userProfile && !userProfile.isGuest) {
    try {
      await clearUserCart(bussinessUuid, userProfile.customerId);
      setCart([]); // Clear local state after successful backend clear
      return true; // Return success
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Failed to clear cart');
      return false; // Return failure
    }
  } else {
    setCart([]);
    localStorage.removeItem(`cart_${bussinessUuid}`);
    return true; // Return success
  }
};

// Updated handleProceedToCheckout
const handleProceedToCheckout = async () => {
  const userProfile = JSON.parse(localStorage.getItem(`user_${bussinessUuid}`) || 'null');
  
  if (!userProfile) {
    setAccountDialogOpen(true);
    toast.info("Please login or create an account to proceed with checkout");
    return;
  }

  if (userProfile.isGuest) {
    setAccountDialogOpen(true);
    toast.info("Please create an account or login to proceed with checkout");
    return;
  }

  // Check B2B minimum order quantities
  if (isB2BMode) {
    const invalidProduct = cart.find((item) => {
      const minQty = item.b2bMinimumOrderQuantity || 0;
      const qty = item.cartQuantity || 1;
      return minQty > 0 && qty < minQty;
    });

    if (invalidProduct) {
      toast.error(
        `${invalidProduct.productName} minimum order quantity is ${invalidProduct.b2bMinimumOrderQuantity}`
      );
      return;
    }
  }

  if (riskControls.blockCheckout || riskControls.approvalMethod === "block") {
    toast.error(`Checkout is blocked for ${customerRiskLevel.toUpperCase()} risk customers.`);
    return;
  }
  
  setDeliveryMethodDialogOpen(true);
};
// In CartPage.jsx - Update handleAccountLogin

const handleAccountLogin = async (user) => {
  try {
    const fingerprint = await getFingerprint();
    const payload = {
      customer_mobile: user.phone,
      name: user.name,
      returnExisting: true,
      fingerprint,
    };

    const response = await createCustomer(bussinessUuid, payload);
    
    if (response?.data) {
      const customerData = response.data;
      
      // Set user profile
      setUserProfile(customerData);
      localStorage.setItem(
        `user_${bussinessUuid}`,
        JSON.stringify(customerData)
      );
      localStorage.removeItem(`guest_${bussinessUuid}`);

      // Merge any guest cart items
      const guestCart = localStorage.getItem(`cart_${bussinessUuid}`);
      if (guestCart) {
        const guestItems = JSON.parse(guestCart);
        for (const item of guestItems) {
          await addToUserCart(bussinessUuid, customerData._id, item);
        }
        localStorage.removeItem(`cart_${bussinessUuid}`);
      }

      // Load user's cart
      const cartResponse = await getUserCart(bussinessUuid, customerData._id);
      if (cartResponse.success && cartResponse.data.items) {
        setCart(cartResponse.data.items);
      }

      // Handle B2B mode
      const isB2BIntent = localStorage.getItem("b2b_intent") === "true";
      if (isB2BIntent) {
        localStorage.setItem("b2b_Price", "true");
      } else {
        localStorage.setItem("b2b_Price", "false");
      }

      // Cleanup
      localStorage.removeItem("b2b_intent");
      localStorage.removeItem("force_b2b_login");
      
      toast.success(`Welcome back, ${customerData.name}!`);
      setAccountDialogOpen(false);
      
      // Check if we need to proceed to checkout
      const shouldProceedToCheckout = sessionStorage.getItem('proceed_to_checkout');
      if (shouldProceedToCheckout) {
        sessionStorage.removeItem('proceed_to_checkout');
        setDeliveryMethodDialogOpen(true);
      }
    }
  } catch (error) {
    console.error('Login error:', error);
    toast.error("Failed to login. Please try again.");
  }
};

// Also update the useEffect that loads user profile to handle guest users better
useEffect(() => {
  const loadUserProfile = () => {
    const savedUser = localStorage.getItem(`user_${bussinessUuid}`);
    const savedGuest = localStorage.getItem(`guest_${bussinessUuid}`);
    
    if (savedUser) {
      setUserProfile(JSON.parse(savedUser));
    } else if (savedGuest) {
      setUserProfile(JSON.parse(savedGuest));
    } else {
      // No user found, set to null
      setUserProfile(null);
    }
  };

  loadUserProfile();
}, [bussinessUuid]);

  // Delivery method selection - MODIFIED: Skip shipping method dialog
  const handleDeliveryMethodSelect = (method) => {
    setDeliveryMethod(method);

    if (method === "home_delivery") {
      setDeliveryAddressConfirmed(false);
      if (userAddresses.length > 0) {
        setAddressSelectionDialogOpen(true);
      } else {
        setAddressDialogOpen(true);
      }
    } else {
      // For pickup, directly open payment dialog
      setDeliveryAddressConfirmed(true);
      setShippingCost(0);
      setShippingMethod("pickup");
      setPaymentDialogOpen(true);
    }
  };

  // Address selection - MODIFIED: Skip shipping method dialog
  const handleAddressSelect = async (address) => {
    const nextPincode = address.postalCode;

    const isSupported = await validateDeliveryPincode(nextPincode);

    if (!isSupported) {
      toast.error("We do not deliver to this location.");
      return false;
    }

    setDeliveryAddress(address.addressLine1);
    setDeliveryCity(address.city);
    setDeliveryState(address.state);
    setDeliveryPincode(nextPincode);
    setDeliveryAddressConfirmed(true);

    setAddressSelectionDialogOpen(false);

    // Skip shipping method dialog and directly open payment dialog
    setShippingMethod("standard"); // Set default shipping method
    setTimeout(() => {
      setPaymentDialogOpen(true);
    }, 500);

    return true;
  };

  // MODIFIED: Comment out shipping method selection handler
  /*
  const handleShippingMethodSelect = (method, cost) => {
    setShippingMethod(method);
    setShippingCost(cost);
    setShippingMethodDialogOpen(false);
    setPaymentDialogOpen(true);
  };
  */

  const handleAddressSubmit = async (addressData) => {
    try {
      const { values, city, state, country, pincode } = addressData;
      const { name, phone, addressLine1, addressLine2, locality, alternatePhone } = values;

      const isSupported = await validateDeliveryPincode(pincode);

      if (!isSupported) {
        toast.error("We do not deliver to this location.");
        return;
      }

      if (userAddresses.length > 0) {
        for (const addr of userAddresses) {
          if (addr.isDefault) {
            await updateAddress(bussinessUuid, userProfile._id, addr._id, {
              isDefault: false,
            });
          }
        }
      }

      const res = await addAddress(bussinessUuid, userProfile._id, {
        name,
        phone,
        addressLine1,
        addressLine2,
        locality,
        alternatePhone,
        city,
        state,
        country,
        postalCode: pincode,
        isDefault: true,
      });

      if (res.data) {
        const newAddress = res.data;

        const isCreatedAddressSupported = await validateDeliveryPincode(
          newAddress.postalCode
        );

        if (!isCreatedAddressSupported) {
          toast.error("We do not deliver to this location.");
          return;
        }

        const updatedAddresses = userAddresses.map((addr) => ({
          ...addr,
          isDefault: false,
        }));
        updatedAddresses.push(newAddress);

        setUserAddresses(updatedAddresses);
        setDeliveryAddress(newAddress.addressLine1);
        setDeliveryCity(newAddress.city);
        setDeliveryState(newAddress.state);
        setDeliveryPincode(newAddress.postalCode);
        setDeliveryAddressConfirmed(true);

        const updatedProfile = {
          ...userProfile,
          addresses: updatedAddresses,
        };

        localStorage.setItem(
          `user_${bussinessUuid}`,
          JSON.stringify(updatedProfile)
        );

        setAddressDialogOpen(false);

        // Skip shipping method dialog and directly open payment dialog
        setShippingMethod("standard"); // Set default shipping method
        setTimeout(() => {
          setPaymentDialogOpen(true);
        }, 500);

        toast.success("New address added and set as default!");
      }
    } catch (err) {
      toast.error("Failed to add address");
    }
  };

  const handleAddNewAddress = () => {
    setAddressSelectionDialogOpen(false);
    setAddressDialogOpen(true);
  };

  const handleClosePaymentMethod = () => setPaymentDialogOpen(false);

  const handlePay = async (method, requestedDeliverySlot = {}) => {
    setPaymentMethod(method);
    setPreferredDeliveryDate(requestedDeliverySlot.preferredDeliveryDate || "");
    setPreferredDeliveryTime(requestedDeliverySlot.preferredDeliveryTime || "");
    setPaymentDialogOpen(false);
    setCheckoutStep("payment");
  };

  // Payment handlers
  const handleRazorpayPayment = async () => {
    try {
      const orderData = {
        customerName: userProfile.name,
        customerEmail: userProfile.email || "",
        customerPhone: userProfile.phone || "",
        orderId: `order_${Date.now()}`,
      };

      const response = await createRazorpayOrder(
        bussinessUuid,
        grandTotal,
        orderData
      );

      const options = {
        key: response.key,
        amount: response.amount,
        currency: response.currency,
        name: store?.businessName || "Store",
        description: "Order Payment",
        order_id: response.orderId,
        handler: async function (razorpayResponse) {
          try {
            const verification = await verifyRazorpayPayment(bussinessUuid, {
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
            });

            if (verification.success) {
              toast.success("Payment successful! Creating your order...");
              await createOrderAfterPayment("razorpay", verification.paymentId);
            } else {
              toast.error("Payment verification failed");
            }
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: userProfile.name,
          email: userProfile.email || "",
          contact: userProfile.phone || "",
        },
        theme: {
          color: storeTheme?.primaryColor || "#3b82f6",
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      toast.error("Failed to initialize payment");
    }
  };

  const handleCashfreePayment = async () => {
    try {
      const customerDetails = {
        customer_id: userProfile.customerId || `guest_${Date.now()}`,
        customer_name: userProfile.name || "Customer",
        customer_email: userProfile.email || "customer@example.com",
        customer_phone:
          userProfile.phone || userProfile.customer_mobile || "9999999999",
      };

      const orderData = {
        customerDetails: customerDetails,
        orderId: `order_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 5)}`,
      };

      const response = await createCashfreeOrder(
        bussinessUuid,
        grandTotal,
        orderData
      );

      if (response.success) {
        const cashfree = new window.Cashfree({
          mode: response.mode || "sandbox",
        });

        cashfree
          .checkout({
            paymentSessionId: response.paymentSessionId,
            redirectTarget: "_modal",
          })
          .then(async (result) => {
            if (result.error) {
              toast.error(`Payment failed: ${result.error.message}`);
              return;
            }

            try {
              const verification = await verifyCashfreePayment(bussinessUuid, {
                orderId: response.orderId,
              });

              if (verification.success) {
                toast.success("Payment successful! Creating your order...");
                await createOrderAfterPayment(
                  "cashfree",
                  verification.paymentId
                );
              } else {
                toast.error("Payment verification failed");
              }
            } catch (verificationError) {
              toast.error("Payment verification error");
            }
          });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Payment initialization failed"
      );
    }
  };
// In your CartPage component
const applyRiskSettingsOverride = (riskLevel, originalAction) => {

  if (!riskSettings) {
    return originalAction;
  }
  
  const riskLevelMap = {
    'low': 'lowRisk',
    'acceptable': 'acceptableRisk',
    'medium': 'mediumRisk',
    'high': 'highRisk',
    'very_high': 'veryHighRisk',
    'critical': 'criticalRisk'
  };
  
  const settingsKey = riskLevelMap[riskLevel];
  const riskLevelSettings = riskSettings[settingsKey];
  
  if (!riskLevelSettings) {
    console.log("No settings found for key:", settingsKey);
    return originalAction;
  }
  
  // Check if blockCheckout is enabled
  if (riskLevelSettings.blockCheckout) {
    console.log("Block checkout is enabled, returning 'block'");
    return "block";
  }
  
  const overriddenAction = riskLevelSettings.approvalMethod;
  console.log("Overridden action from settings:", overriddenAction);
  
  // If overridden action exists and is different, use it
  if (overriddenAction && overriddenAction !== originalAction) {
    console.log(`✅ OVERRIDE APPLIED: ${originalAction} -> ${overriddenAction}`);
    return overriddenAction;
  }
  
  console.log("No override needed, using original action:", originalAction);
  return originalAction;
};
// Function to get action description
const getActionDescription = (action) => {
  const actions = {
    'approve': { message: "Order will be created immediately.", requiresOtp: false, requiresManualReview: false, isBlocked: false },
    'approve_with_log': { message: "Order will be created and logged for monitoring.", requiresOtp: false, requiresManualReview: false, isBlocked: false },
    'otp_verify': { message: "OTP verification required before order creation.", requiresOtp: true, requiresManualReview: false, isBlocked: false },
    'manual_review': { message: "Order will be created but requires manual review.", requiresOtp: false, requiresManualReview: true, isBlocked: false },
    'block': { message: "Order will be blocked.", requiresOtp: false, requiresManualReview: false, isBlocked: true }
  };
  return actions[action] || actions['approve'];
};
const createOrderAfterPayment = async (
  paymentMethod,
  transactionId = null
) => {
  if (isPlacingOrder) {
    return;
  }

  setIsPlacingOrder(true);

  try {
if (!ensureDeliveryLocationSupported()) {
  return;
}
const { subtotal, taxAmount, grandTotal, discount, updatedCart } = calculateOrderTotals();
    const formatDateDDMMYYYY = (timestamp) => {
      const date = new Date(timestamp);
      return `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${date.getFullYear()}`;
    };

    const storeState = store?.state || "";
    // const updatedCart = cart.map((item) =>
    //   calculateGSTForItem(item, deliveryState, storeState)
    // );

    // Calculate totals
    // const taxAmount = Number(
    //   updatedCart
    //     .reduce((acc, item) => {
    //       const itemTax =
    //         item.taxBreakup.cgst +
    //         item.taxBreakup.sgst +
    //         item.taxBreakup.igst +
    //         item.taxBreakup.cess;
    //       return acc + itemTax;
    //     }, 0)
    //     .toFixed(2)
    // );

    // const subtotal = Number(
    //   updatedCart.reduce((acc, item) => acc + item.baseAmount, 0).toFixed(2)
    // );

    // const grandTotal = Number(
    //   (subtotal + taxAmount + shippingCost).toFixed(2)
    // );

    const orderId = getSixDigitHexTimestamp();
    setOrderId(orderId);

    // FIXED: Include ALL variant data in items
    const itemsForAPI = JSON.stringify(
      updatedCart.map((item) => {
        const price = isB2BMode
          ? item.b2bSalePrice ?? item.salePrice
          : item.salePrice;
        
        // Extract variant attributes
        let variantAttributes = [];
        if (item.variantAttributes && item.variantAttributes.length > 0) {
          variantAttributes = item.variantAttributes;
        } else if (item.selectedVariant?.attributes) {
          variantAttributes = item.selectedVariant.attributes;
        }
        
        const formattedAttributes = variantAttributes.map(attr => ({
          type: String(attr.type || '').toLowerCase(),
          value: String(attr.value || '')
        })).filter(attr => attr.type && attr.value);
        
        const variantKey = formattedAttributes.length > 0 
          ? formattedAttributes
              .map(attr => `${attr.type}:${attr.value}`.toLowerCase().trim())
              .sort()
              .join('|')
          : null;
        
        return {
          // Core fields
          _id: item._id,
          productId: item.productId || item._id,
          productName: item.productName,
          sku_number: item.sku_number || '',
          salePrice: Number(price || 0),
          price: Number(price || 0),
          cartQuantity: item.cartQuantity || 1,
          quantity: item.cartQuantity || 1,
          
          // Tax fields
          weight: item.weight || 0.5,
          taxable_v: item.taxable_v || 'taxable',
          cgst: item.cgst || 0,
          sgst_utgst: item.sgst_utgst || 0,
          igst: item.igst || 0,
          cess: item.cess || 0,
          cgstRate: item.cgst || 0,
          sgstRate: item.sgst_utgst || 0,
          igstRate: item.igst || 0,
          cessRate: item.cess || 0,
          taxCalculation: item.taxBreakup,
          
          // CRITICAL: Variant fields
          hasVariants: formattedAttributes.length > 0 || item.hasVariants || false,
          variantId: item.variantId || item.selectedVariant?.variantId || null,
          variantName: item.variantName || item.selectedVariant?.variantName || 
            (formattedAttributes.length > 0 ? formattedAttributes.map(a => a.value).join(' - ') : null),
          variantAttributes: formattedAttributes, // THIS IS THE KEY
          variantSku: item.variantSku || item.selectedVariant?.sku || null,
          variantStock: item.variantStock || item.selectedVariant?.quantity || 0,
          variantSalePrice: item.variantSalePrice || item.selectedVariant?.salePrice || Number(price || 0),
          variantPurchasePrice: item.variantPurchasePrice || item.selectedVariant?.purchasePrice || null,
          variantMrp: item.variantMrp || item.selectedVariant?.mrp || null,
          variantBatchNumber: item.variantBatchNumber || item.selectedVariant?.batchNumber || null,
          variantKey: item.variantKey || variantKey,

          showReturn: !riskControls.disableReturns,
          returnPeriod: riskControls.disableReturns ? "0_days" : "7_days",
          
          isB2B: isB2BMode,
        };
      })
    );

// const itemsForAPI = JSON.stringify(
//   updatedCart.map((item) => {
//     const price = isB2BMode
//       ? item.b2bSalePrice ?? item.salePrice
//       : item.salePrice;
    
//     // Extract variant attributes
//     let variantAttributes = [];
//     if (item.variantAttributes && item.variantAttributes.length > 0) {
//       variantAttributes = item.variantAttributes;
//     } else if (item.selectedVariant?.attributes) {
//       variantAttributes = item.selectedVariant.attributes;
//     }
    
//     const formattedAttributes = variantAttributes.map(attr => ({
//       type: String(attr.type || '').toLowerCase(),
//       value: String(attr.value || '')
//     })).filter(attr => attr.type && attr.value);
    
//     const variantKey = formattedAttributes.length > 0 
//       ? formattedAttributes
//           .map(attr => `${attr.type}:${attr.value}`.toLowerCase().trim())
//           .sort()
//           .join('|')
//       : null;
    
//     // CRITICAL: Include the taxBreakup from calculateGSTForItem
//     const taxBreakup = item.taxBreakup || {
//       cgst: 0,
//       sgst: 0,
//       igst: 0,
//       cess: 0
//     };
    
//     return {
//       // Core fields
//       _id: item._id,
//       productId: item.productId || item._id,
//       productName: item.productName,
//       sku_number: item.sku_number || '',
//       salePrice: Number(price || 0),
//       price: Number(price || 0),
//       cartQuantity: item.cartQuantity || 1,
//       quantity: item.cartQuantity || 1,
      
//       // Tax fields - send rates
//       weight: item.weight || 0.5,
//       taxable_v: item.taxable_v || 'taxable',
//       cgst: item.cgst || 0,
//       sgst_utgst: item.sgst_utgst || 0,
//       igst: item.igst || 0,
//       cess: item.cess || 0,
//       cgstRate: item.cgst || 0,
//       sgstRate: item.sgst_utgst || 0,
//       igstRate: item.igst || 0,
//       cessRate: item.cess || 0,
      
//       // CRITICAL: Send the calculated tax amounts
//       baseAmount: item.baseAmount || Number(price || 0) * (item.cartQuantity || 1),
//       taxBreakup: taxBreakup, // This tells backend the actual tax amounts
//       calculatedTax: {
//         cgst: taxBreakup.cgst || 0,
//         sgst: taxBreakup.sgst || 0,
//         igst: taxBreakup.igst || 0,
//         cess: taxBreakup.cess || 0,
//         totalTax: (taxBreakup.cgst || 0) + (taxBreakup.sgst || 0) + (taxBreakup.igst || 0) + (taxBreakup.cess || 0)
//       },
      
//       // Variant fields
//       hasVariants: formattedAttributes.length > 0 || item.hasVariants || false,
//       variantId: item.variantId || item.selectedVariant?.variantId || null,
//       variantName: item.variantName || item.selectedVariant?.variantName || 
//         (formattedAttributes.length > 0 ? formattedAttributes.map(a => a.value).join(' - ') : null),
//       variantAttributes: formattedAttributes,
//       variantSku: item.variantSku || item.selectedVariant?.sku || null,
//       variantStock: item.variantStock || item.selectedVariant?.quantity || 0,
//       variantSalePrice: item.variantSalePrice || item.selectedVariant?.salePrice || Number(price || 0),
//       variantPurchasePrice: item.variantPurchasePrice || item.selectedVariant?.purchasePrice || null,
//       variantMrp: item.variantMrp || item.selectedVariant?.mrp || null,
//       variantBatchNumber: item.variantBatchNumber || item.selectedVariant?.batchNumber || null,
//       variantKey: item.variantKey || variantKey,
      
//       isB2B: isB2BMode,
//     };
//   })
// );
 

    const fingerprint = await getFingerprint();
    const dataForCreate = {
      customerName: userProfile?.name || "Guest Customer",
      customerId: userProfile?.customerId || userProfile?._id || `guest_${Date.now()}`,
      phone: userProfile?.phone || userProfile?.customer_mobile || "",
      address: deliveryAddress,
      city: deliveryCity,
      state: deliveryState,
      pincode: deliveryPincode,
      preferredDeliveryDate,
      preferredDeliveryTime,
      items: itemsForAPI,
      bussinessUuid: bussinessUuid,
      orderId: orderId,
      total: grandTotal,
      subtotal: subtotal,
      taxAmount: taxAmount,
      shippingCost: shippingCost,
      discount: discount,
      orderCouponName: appliedCoupon?.name || null,
      couponType: appliedCoupon?.type || null,
      paymentMethod: paymentMethod,
      paymentStatus: paymentMethod === "cod" ? "pending" : "paid",
      orderStatus: paymentMethod === "cod" ? "Ordered" : "Confirmed",
      transactionId: transactionId || null,
      fingerprint,
      attribution: getTrackingContext(),
    };

    await createOrders(bussinessUuid, dataForCreate);
    const trackingContext = getTrackingContext();
    if (trackingContext?.sessionId) {
      await linkGuestCustomer({
        sessionId: trackingContext.sessionId,
        customerId: userProfile.customerId,
        customerMobile: userProfile.customer_mobile || "",
        customerName: userProfile.name || "",
      });
      trackPurchase({
        orderId: dataForCreate.orderId,
        amount: grandTotal,
        businessUuid: bussinessUuid,
        items: updatedCart,
      });
      syncTracking("purchase");
    }
    const cartCleared = await emptyCart(); // Wait for cart to clear
    
    if (cartCleared) {
      setPaymentDialogOpen(false);
      setCheckoutStep("confirmation");
      setOrderConfirmationDialogOpen(true);
      toast.success("Order placed successfully!");
    } else {
      setPaymentDialogOpen(false);
      setCheckoutStep("confirmation");
      setOrderConfirmationDialogOpen(true);
      toast.success("Order placed successfully! (Cart cleanup pending)");
    }

  } catch (error) {
    toast.error("Failed to create order after payment: " + error.message);
  } finally {
    setIsPlacingOrder(false);
  }
};
const handleConfirmOrder = async () => {
  if (isPlacingOrder) return;

  setIsPlacingOrder(true);

  try {
    if (!ensureDeliveryLocationSupported()) return;

    const { subtotal, taxAmount, grandTotal, discount, updatedCart } = calculateOrderTotals();
    
    let flaskRiskLevel = "low";
    let flaskRecommendedAction = "approve";
    let flaskScore = 0;
    let flaskReasons = [];
    let flaskDimensionScores = {};
    let flaskFlags = {};
    
    // STEP 1: Get risk assessment from Flask backend (PRIMARY SOURCE)
    try {
      const riskItems = updatedCart.map(item => ({
        productId: item.productId || item._id,
        productName: item.productName,
        quantity: item.cartQuantity || 1,
        price: isB2BMode ? (item.b2bSalePrice ?? item.salePrice) : item.salePrice,
        productCategory: item.productCategory || ''
      }));
      
      const riskPayload = {
        order: {
          customerId: userProfile?.customerId || userProfile?._id,
          total: grandTotal,
          paymentMethod: paymentMethod,
          pincode: deliveryPincode,
          address: deliveryAddress,
          city: deliveryCity,
          state: deliveryState,
          items: riskItems,
          phone: userProfile?.phone || userProfile?.customer_mobile || "",
          orderId: `temp_${Date.now()}`
        },
        sessionId: getTrackingContext()?.sessionId,
        fingerprintId: await getFingerprint(),
        is_cod: paymentMethod === "cod",
        coupon_used: !!appliedCoupon,
        deviceType: /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? "mobile" : "web"
      };
      
      const riskResponse = await fetch(`https://risktest.cogitofs.com/api/v1/risk/${bussinessUuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(riskPayload)
      });
      
      if (!riskResponse.ok) {
        throw new Error(`Flask API returned ${riskResponse.status}`);
      }
      
      const riskAssessment = await riskResponse.json();
      
      flaskScore = riskAssessment.final_score || 0;
      flaskRiskLevel = riskAssessment.risk_level || "low";
      flaskRecommendedAction = riskAssessment.recommended_action || "approve";
      flaskDimensionScores = riskAssessment.dimension_scores || {};
      flaskFlags = riskAssessment.flags || {};
      
      if (Array.isArray(riskAssessment.reasons)) {
        flaskReasons = riskAssessment.reasons;
      } else if (riskAssessment.reasons && typeof riskAssessment.reasons === 'object') {
        flaskReasons = [];
        Object.keys(riskAssessment.reasons).forEach(key => {
          if (Array.isArray(riskAssessment.reasons[key])) {
            flaskReasons.push(...riskAssessment.reasons[key]);
          } else if (typeof riskAssessment.reasons[key] === 'string') {
            flaskReasons.push(riskAssessment.reasons[key]);
          }
        });
      } else if (typeof riskAssessment.reasons === 'string') {
        flaskReasons = [riskAssessment.reasons];
      } else {
        flaskReasons = [`Risk score: ${flaskScore}`];
      }
      
      console.log(`📊 Risk Assessment Result - Score: ${flaskScore}, Level: ${flaskRiskLevel}, Action: ${flaskRecommendedAction}`);
      
    } catch (riskError) {
      console.error("Flask risk assessment error:", riskError);
      toast.error("Risk assessment service unavailable. Please try again later.");
      setIsPlacingOrder(false);
      return;
    }

    // STEP 2: Load risk settings overrides from Risk Management Page
    let riskSettings = null;
    let overriddenAction = null;
    let isOverridden = false;
    
    try {
      const settingsResponse = await getRiskSettings(bussinessUuid);
      if (settingsResponse.success && settingsResponse.data) {
        riskSettings = settingsResponse.data;
      }
    } catch (error) {
      console.error("Failed to load risk settings:", error);
    }

    // STEP 3: Apply override ONLY if explicitly set AND not "follow_backend"
    const riskLevelKeyMap = {
      'low': 'lowRisk',
      'acceptable': 'acceptableRisk',
      'medium': 'mediumRisk',
      'high': 'highRisk',
      'very_high': 'veryHighRisk',
      'critical': 'criticalRisk'
    };
    
    const riskLevelKey = riskLevelKeyMap[flaskRiskLevel];
    const levelSettings = riskSettings?.[riskLevelKey];
    
    let finalAction = flaskRecommendedAction;
    
    if (levelSettings && levelSettings.approvalMethod && levelSettings.approvalMethod !== "follow_backend") {
      overriddenAction = levelSettings.approvalMethod;
      isOverridden = true;
      finalAction = overriddenAction;
      console.log(`⚠️ OVERRIDE APPLIED: Flask wanted ${flaskRecommendedAction}, using ${finalAction} from Risk Management settings`);
    } else {
      console.log(`✅ Using Flask recommendation: ${finalAction} for risk level ${flaskRiskLevel}`);
    }
    
    // Check if COD is disabled (from settings)
    if (levelSettings && levelSettings.disableCod === true && paymentMethod === "cod") {
      toast.error(`Cash on Delivery is disabled for ${flaskRiskLevel.toUpperCase()} risk orders. Please use online payment.`);
      setIsPlacingOrder(false);
      return;
    }
    
    // Check if checkout is blocked (from settings)
    if (levelSettings && levelSettings.blockCheckout === true) {
      toast.error(`Checkout is blocked for ${flaskRiskLevel.toUpperCase()} risk orders.`);
      setIsPlacingOrder(false);
      return;
    }
    
    const requiresOtp = finalAction === "otp_verify";
    const requiresManualReview = finalAction === "manual_review";
    const isBlocked = finalAction === "block";
    
    // Block if needed
    if (isBlocked) {
      const reasonText = flaskReasons.length > 0 ? flaskReasons.join(', ') : `Risk score: ${flaskScore}`;
      toast.error(`Order blocked: ${reasonText}`);
      setIsPlacingOrder(false);
      return;
    }
    
    // Show appropriate message based on action
    if (requiresOtp) {
      console.log("🔐 OTP REQUIRED - Opening CAPTCHA verification dialog...");
      toast.info(`Security verification required. Risk score: ${flaskScore}`);
      
      // Show OTP dialog - NO PHONE NUMBER NEEDED, just CAPTCHA
      const otpVerified = await showOtpVerification();
      
      if (!otpVerified) {
        toast.error("Order cancelled - Security verification failed");
        setIsPlacingOrder(false);
        return;
      }
      
      toast.success("Security verification completed!");
    } else if (requiresManualReview) {
      toast.warning(`Order requires manual review. Risk score: ${flaskScore}`);
    } else if (finalAction === 'approve_with_log') {
      toast.info(`Order approved with monitoring. Risk score: ${flaskScore}`);
    } else if (finalAction === 'approve') {
      console.log(`✅ Order approved. Risk score: ${flaskScore}`);
    }
    
    // STEP 5: Create the order with appropriate status
    const orderId = getSixDigitHexTimestamp();
    setOrderId(orderId);
    
    // Prepare order items
    const itemsForAPI = JSON.stringify(updatedCart.map((item) => {
      const price = isB2BMode ? (item.b2bSalePrice ?? item.salePrice) : item.salePrice;
      
      let variantAttributes = [];
      if (item.variantAttributes && item.variantAttributes.length > 0) {
        variantAttributes = item.variantAttributes;
      } else if (item.selectedVariant?.attributes) {
        variantAttributes = item.selectedVariant.attributes;
      }
      
      const formattedAttributes = variantAttributes.map(attr => ({
        type: String(attr.type || '').toLowerCase(),
        value: String(attr.value || '')
      })).filter(attr => attr.type && attr.value);
      
      let variantKey = null;
      if (formattedAttributes.length > 0) {
        variantKey = formattedAttributes
          .map(attr => `${attr.type}:${attr.value}`.toLowerCase().trim())
          .sort()
          .join('|');
      }
      
      return {
        _id: item._id,
        productId: item.productId || item._id,
        productName: item.productName,
        salePrice: Number(price || 0),
        price: Number(price || 0),
        cartQuantity: item.cartQuantity || 1,
        quantity: item.cartQuantity || 1,
        cgst: item.cgst || 0,
        sgst_utgst: item.sgst_utgst || 0,
        igst: item.igst || 0,
        cess: item.cess || 0,
        taxBreakup: item.taxBreakup,
        baseAmount: item.baseAmount,
        hasVariants: formattedAttributes.length > 0 || item.hasVariants || false,
        variantId: item.variantId,
        variantName: item.variantName,
        variantAttributes: formattedAttributes,
        variantKey: variantKey,
        variantSku: item.variantSku,
        variantStock: item.variantStock,
        variantSalePrice: item.variantSalePrice,
        variantPurchasePrice: item.variantPurchasePrice,
        variantMrp: item.variantMrp,
        variantBatchNumber: item.variantBatchNumber,
        showReturn: true,
        returnPeriod: "7_days",
        isB2B: isB2BMode,
      };
    }));
    
    const fingerprint = await getFingerprint();
    
    // ========== CRITICAL: Set order status based on FINAL action ==========
    let orderStatus;
    
    switch(finalAction) {
      case "approve":
      case "approve_with_log":
        orderStatus = "Confirmed";
        break;
      case "otp_verify":
        orderStatus = "Verified";
        break;
      case "manual_review":
        orderStatus = "Pending Review";
        break;
      case "block":
        orderStatus = "Cancelled";
        break;
      default:
        orderStatus = "Ordered";
    }
    
    console.log(`📝 Creating Order: ${orderId}`);
    console.log(`   Flask Level: ${flaskRiskLevel} | Flask Action: ${flaskRecommendedAction}`);
    console.log(`   Final Action: ${finalAction} | Order Status: ${orderStatus}`);
    console.log(`   Override Applied: ${isOverridden ? `Yes (to ${overriddenAction})` : "No"}`);
    
    const orderData = {
      customerName: userProfile?.name || "Guest Customer",
      customerId: userProfile?.customerId || userProfile?._id || `guest_${Date.now()}`,
      phone: userProfile?.phone || userProfile?.customer_mobile || "",
      address: deliveryAddress,
      city: deliveryCity,
      state: deliveryState,
      pincode: deliveryPincode,
      country: "India",
      preferredDeliveryDate,
      preferredDeliveryTime,
      items: itemsForAPI,
      bussinessUuid: bussinessUuid,
      orderId: orderId,
      total: grandTotal,
      subtotal: subtotal,
      taxAmount: taxAmount,
      shippingCost: shippingCost,
      discount: discount,
      orderCouponName: appliedCoupon?.name || null,
      couponType: appliedCoupon?.type || null,
      paymentMethod: paymentMethod,
      paymentStatus: "pending",
      orderStatus: orderStatus,
      fingerprint,
      attribution: getTrackingContext(),
      riskAssessment: {
        score: flaskScore,
        level: flaskRiskLevel,
        flaskAction: flaskRecommendedAction,
        finalAction: finalAction,
        reasons: flaskReasons,
        dimensionScores: flaskDimensionScores,
        flags: flaskFlags,
        overrideApplied: isOverridden,
        overrideValue: overriddenAction
      },
    };
    
    await createOrders(bussinessUuid, orderData);
    
    const trackingContext = getTrackingContext();
    if (trackingContext?.sessionId) {
      await linkGuestCustomer({
        sessionId: trackingContext.sessionId,
        customerId: userProfile?.customerId,
        customerMobile: userProfile?.customer_mobile || userProfile?.phone,
        customerName: userProfile?.name || "",
      });
      trackPurchase({
        orderId: orderId,
        amount: grandTotal,
        businessUuid: bussinessUuid,
        items: updatedCart,
      });
      syncTracking("purchase");
    }
    
    await emptyCart();
    setCheckoutStep("confirmation");
    setOrderConfirmationDialogOpen(true);
    
    // Success message based on final action
    if (finalAction === "manual_review") {
      toast.success("Order placed successfully! It will be processed after manual review.");
    } else if (finalAction === 'approve_with_log') {
      toast.success("Order placed successfully and logged for monitoring!");
    } else if (finalAction === 'approve') {
      toast.success("Order placed successfully!");
    } else if (finalAction === 'otp_verify') {
      toast.success("Order verified and placed successfully!");
    } else {
      toast.success("Order placed successfully!");
    }
    
  } catch (error) {
    console.error('Order placement error:', error);
    toast.error(error?.response?.data?.message || "Failed to place order. Please try again.");
  } finally {
    setIsPlacingOrder(false);
  }
};
  const handleOrderConfirmationClose = () => {
    setOrderConfirmationDialogOpen(false);
    navigate(
      getStorefrontPath({
        businessName: displayBusinessName,
        bussinessUuid,
      }),
    );
  };

  // Cleanup function for debouncing
  useEffect(() => {
    return () => {
      // Clean up all pending timeouts
      updateTimeouts.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      // Cancel any pending debounced calls
      debouncedUpdateCart.cancel();
    };
  }, [debouncedUpdateCart]);

  // Calculate totals for display using the shared cart totals helper
  const { subtotal, taxAmount, grandTotal, discount, updatedCart } = calculateOrderTotals();

  const confirmOrderRef = useRef(null);

  useEffect(() => {
    if (checkoutStep === "payment" && confirmOrderRef.current) {
      confirmOrderRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [checkoutStep]);
  
  const orderValue = cart.reduce((sum, item) => {
    // return sum + (item.salePrice || 0) * (item.cartQuantity || 1);
     const price = isB2BMode
    ? item.b2bSalePrice ?? item.salePrice 
    : item.salePrice;

  return sum + (price || 0) * (item.cartQuantity || 1);
  }, 0);

  const orderWeight = cart.reduce((sum, item) => {
    return sum + (item.weight || 0.5) * (item.cartQuantity || 1);
  }, 0) || 1;
const renderCartItems = () => (
  <div className='p-4'>
    <div className={`max-h-80 overflow-y-auto pr-2 custom-scrollbar space-y-3 ${isDarkMode ? 'dark-scrollbar' : ''}`}>
      {cart.map((item) => {
        const price = isB2BMode
          ? Number(item.b2bSalePrice ?? item.salePrice ?? 0)
          : Number(item.salePrice ?? 0);
        
        {/* const itemTotal = price * (item.cartQuantity || 1); */}
         const itemTotal = (Number(item.salePrice) || 0) * (Number(item.cartQuantity) || 1);
        const storeState = store?.state || "";
        const calculatedItem = calculateGSTForItem(
          item,
          deliveryState,
          storeState
        );
        const itemTax =
          (calculatedItem.taxBreakup.cgst || 0) +
          (calculatedItem.taxBreakup.sgst || 0) +
          (calculatedItem.taxBreakup.igst || 0) +
          (calculatedItem.taxBreakup.cess || 0);

        // ========== NEW: Extract variant display info ==========
        // Get variant attributes from multiple possible sources
        let variantAttributes = [];
        let variantDisplayText = '';
        
        if (item.variantAttributes && item.variantAttributes.length > 0) {
          variantAttributes = item.variantAttributes;
        } else if (item.selectedVariant?.attributes && item.selectedVariant.attributes.length > 0) {
          variantAttributes = item.selectedVariant.attributes;
        } else if (item.attributes && item.attributes.length > 0) {
          variantAttributes = item.attributes;
        }
        
        // Format variant attributes for display
        if (variantAttributes.length > 0) {
          variantDisplayText = variantAttributes
            .map(attr => `${attr.type}: ${attr.value}`)
            .join(' • ');
        } else if (item.variantName && item.variantName !== item.productName) {
          variantDisplayText = item.variantName;
        } else if (item.selectedVariant?.variantName) {
          variantDisplayText = item.selectedVariant.variantName;
        }
        
        // Check if item has variants
        const hasVariants = item.hasVariants || 
                           variantAttributes.length > 0 || 
                           !!item.variantId ||
                           !!item.selectedVariant;
        // =======================================================

        return (
          <div
            key={item._id}
            className={`flex items-center gap-4 p-4 rounded-lg border shadow-sm hover:shadow transition-all duration-200 ${
              isDarkMode
                ? 'border-slate-700 bg-slate-900'
                : 'border-gray-200 bg-white'
            }`}
            style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}
          >
            <div className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
              {(item.image || item.itemPicture) ? (
                <img
                  src={AWS_URL + (item.image || item.itemPicture)}
                  alt={item.productName}
                  className='w-full h-full object-cover'
                />
              ) : (
                <div className={`w-full h-full flex items-center justify-center ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                  <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>No Image</span>
                </div>
              )}
            </div>

            <div className='flex-1 min-w-0'>
              <h3 className='font-medium text-sm truncate'
                  style={{ color: headingStyle.color }}>
                {item.productName}
              </h3>
              
              {/* ========== NEW: Show variant attributes ========== */}
              {hasVariants && variantDisplayText && (
                <div className='mt-1 space-y-1'>
                  {/* Variant Name/Pills Display */}
                  <div className='flex flex-wrap gap-1'>
                    {variantAttributes.length > 0 ? (
                      variantAttributes.map((attr, idx) => {
                        // Special styling for color attributes
                        if (attr.type.toLowerCase() === 'color') {
                          return (
                            <div
                              key={idx}
                              className='inline-flex items-center px-2 py-0.5 rounded-full text-xs border'
                              style={{
                                backgroundColor: `${attr.value}20`,
                                borderColor: attr.value,
                                color: '#374151'
                              }}
                              title={`${attr.type}: ${attr.value}`}
                            >
                              <span 
                                className='w-2 h-2 rounded-full mr-1'
                                style={{ backgroundColor: attr.value }}
                              ></span>
                              {attr.value}
                            </div>
                          );
                        }
                        // For size and other attributes
                        return (
                          <span
                            key={idx}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                              isDarkMode
                                ? 'bg-slate-800 text-slate-200 border-slate-600'
                                : 'bg-gray-100 text-gray-700 border-gray-200'
                            }`}
                          >
                            <span className='font-medium mr-1'>{attr.type}:</span>
                            {attr.value}
                          </span>
                        );
                      })
                    ) : (
                      <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200'>
                        {variantDisplayText}
                      </span>
                    )}
                  </div>
                
                </div>
              )}
              {/* ================================================== */}

                <p className='text-xs mt-1'
                  style={{ color: isDarkMode ? '#cbd5e1' : (storeTheme?.textColor || '#6b7280') }}>
                ₹{price}
              </p>

              {checkoutStep === "payment" && itemTax > 0 && (
                <p className='text-xs text-green-600 mt-0.5'>
                  Tax: ₹{itemTax.toFixed(2)}
                </p>
              )}

              <div className='flex items-center mt-2'>
                <button
                  className={`w-6 h-6 flex items-center justify-center border rounded-l transition-colors ${
                    isDarkMode
                      ? 'border-slate-600 bg-slate-800 hover:bg-slate-700 text-slate-200'
                      : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                  }`}
                  onClick={() => handleDecrement(item._id)}
                  disabled={item.cartQuantity <= 1}
                >
                  <MdRemove size={14} />
                </button>
                <span className={`w-8 text-center border-t border-b h-6 flex items-center justify-center text-sm font-medium ${
                  isDarkMode
                    ? 'border-slate-600 bg-slate-800 text-slate-100'
                    : 'border-gray-300 bg-gray-50'
                }`}>
                  {item.cartQuantity || 1}
                </span>
                <button
                  className={`w-6 h-6 flex items-center justify-center border rounded-r transition-colors ${
                    isDarkMode
                      ? 'border-slate-600 bg-slate-800 hover:bg-slate-700 text-slate-200'
                      : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                  }`}
                  onClick={() => handleIncrement(item._id)}
                >
                  <MdAdd size={14} />
                </button>
              </div>
            </div>

            <div className='text-right flex flex-col items-end'>
              <p className='text-base font-bold'
                 style={{ color: headingStyle.color }}>
                {/* ₹
                {checkoutStep === "cart"
                  ? itemTotal
                  : calculatedItem.totalWithTax} */}
                   ₹{checkoutStep === "cart"
                  ? itemTotal.toFixed(2)
                  : (calculatedItem.totalWithTax || 0).toFixed(2)
                }
              </p>
              <button
                onClick={() => handleRemoveFromCart(item._id)}
                className='mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-3 w-3'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                  />
                </svg>
                Remove
              </button>
            </div>
          </div>
        );
      })}
    </div>

    <style>
      {`
      .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #c5c5c5;
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }
      .dark-scrollbar::-webkit-scrollbar-track {
        background: #1f2937;
      }
      .dark-scrollbar::-webkit-scrollbar-thumb {
        background: #475569;
      }
      .dark-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #64748b;
      }
    `}
    </style>
  </div>
);
  // MODIFIED: Remove shipping info section since shipping is free
  const renderShippingInfo = () => {
    return null; // Don't show shipping info
  };
  
  const renderOrderSummary = () => {
    const storeState = store?.state || "";
    const { subtotal, taxAmount, grandTotal, discount, updatedCart } = calculateOrderTotals();
    const deliveryRestricted = deliveryMethod === "home_delivery" && isPincodeDeliveryEnabled && !isDeliveryPincodeSupported(deliveryPincode);
    // const updatedCart = cart.map((item) =>
    //   calculateGSTForItem(item, deliveryState, storeState)
    // );

    // const subtotal = Number(
    //   updatedCart
    //     .reduce((acc, item) => acc + (item.baseAmount || 0), 0)
    //     .toFixed(2)
    // );

    // const taxAmount = Number(
    //   updatedCart
    //     .reduce((acc, item) => {
    //       const itemTax =
    //         (item.taxBreakup?.cgst || 0) +
    //         (item.taxBreakup?.sgst || 0) +
    //         (item.taxBreakup?.igst || 0) +
    //         (item.taxBreakup?.cess || 0);
    //       return acc + itemTax;
    //     }, 0)
    //     .toFixed(2)
    // );

    // const grandTotal = Number((subtotal + taxAmount + shippingCost).toFixed(2));
const handleApplyCoupon = async () => {
  try {
    if (!couponCode) {
      toast.error("Enter Coupon Code");
      return;
    }

    setCouponLoading(true);
    const payload = {
      couponName: couponCode,
      cartItems: cart.map(item => ({
        productId: item._id,
        qty: item.quantity,
        price: item.salePrice
      })),
      orderTotal: subtotal + taxAmount + shippingCost
    };

    const res = await applyCoupon(bussinessUuid, payload);

    // setCouponDiscount(res.data.discount);
    setCouponDiscount(Number(res.data.discount || 0));


    setAppliedCoupon({
      name: res.data.appliedCoupon,
      type: res.data.couponType
    });

    toast.success("Coupon Applied Successfully");

  } catch (err) {
    toast.error(err?.response?.data?.message || "Coupon Error");
    setCouponDiscount(0);
    setAppliedCoupon(null);
  } finally {
    setCouponLoading(false);
  }
};
const handleRemoveCoupon = () => {
  setAppliedCoupon(null);
  setCouponDiscount(0);
  setCouponCode("");
};
    if (cart.length === 0) {
      return (
        <div className={`${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} rounded-xl shadow-md p-6`}
             style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
          <h3 className='text-lg font-semibold mb-4'
              style={{ color: headingStyle.color }}>
            Order Summary
          </h3>
          <div className='text-center py-8'>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
              <MdShoppingCart className={`text-2xl ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
            </div>
            <p
               style={{ color: isDarkMode ? '#cbd5e1' : (storeTheme?.textColor || '#6b7280') }}>
              Your cart is empty
            </p>
            <p className='text-sm mt-1'
               style={{ color: isDarkMode ? '#94a3b8' : (storeTheme?.textColor + '80' || '#9ca3af') }}>
              Add items to see order summary
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={`${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} rounded-xl shadow-md p-6`}
           style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
            <div className="mt-4 space-y-2">
  <input
    type="text"
    value={couponCode}
    onChange={(e) => setCouponCode(e.target.value)}
    placeholder="Enter Coupon Code"
    className="border p-2 w-full rounded"
  />

  <div className="flex gap-2">
    <button
      onClick={handleApplyCoupon}
      disabled={couponLoading}
      className="bg-green-600 text-white px-4 py-2 rounded"
    >
      Apply Coupon
    </button>

    {appliedCoupon && (
      <button
        onClick={handleRemoveCoupon}
        className="bg-red-600 text-white px-4 py-2 rounded"
      >
        Remove
      </button>
    )}
  </div>
</div>
        <h3 className='text-lg font-semibold mb-4'
            style={{ color: headingStyle.color }}>
          {t("CART_MODEL.ORDER_SUMMARY")} ({cart.length} {cart.length === 1 ? "item" : "items"})
        </h3>

        <div className='space-y-3'>
          <div className='flex justify-between'>
            <span style={{ color: isDarkMode ? '#cbd5e1' : (storeTheme?.textColor || '#6b7280') }}>
              {t("NEWINVOICE.SUB_TOTAL")}
            </span>
            <span className='font-medium'>₹{subtotal.toFixed(2)}</span>
          </div>
          {(deliveryMethod !== "home_delivery" || deliveryAddressConfirmed) && (
            <div className='flex justify-between'>
              <span className='text-gray-600'
                    style={{ color: storeTheme?.textColor || '#6b7280' }}>
                {t("ONLINE_STORE.TAX")} (GST)
              </span>
              <span className='font-medium'>₹{taxAmount.toFixed(2)}</span>
            </div>
          )}
          {appliedCoupon && couponDiscount > 0 && (
  <div className="flex justify-between text-green-700 mt-3">
    <span>
      Coupon Discount ({appliedCoupon?.name})
    </span>
    <span>
      - ₹{Number(couponDiscount || 0).toFixed(2)}
    </span>
  </div>
)}
          {(deliveryMethod !== "home_delivery" || deliveryAddressConfirmed) && (
            <div className='flex justify-between'>
              <span className='text-gray-600'
                    style={{ color: storeTheme?.textColor || '#6b7280' }}>
                {t("CARTEXTRA.SHIPPING")}
              </span>
              {shippingCost === 0 ? (
                <span className='font-medium text-green-600'>Free</span>
              ) : (
                <span className='font-medium'>₹{shippingCost.toFixed(2)}</span>
              )}
            </div>
          )}
          <div className='border-t border-gray-200 pt-3'>
            <div className='flex justify-between text-lg font-bold'>
              <span style={{ color: headingStyle.color }}>{t("CART_MODEL.TOTAL")}</span>
              <span style={{ color: storeTheme?.primaryColor || '#3b82f6' }}>₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {taxAmount > 0 && (
          <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
            <h4 className='text-sm font-semibold mb-2'
                style={{ color: headingStyle.color }}>
              Tax Breakdown
            </h4>
            <div className='text-xs space-y-1'
                 style={{ color: isDarkMode ? '#cbd5e1' : (storeTheme?.textColor || '#6b7280') }}>
              <div className='flex justify-between'>
                <span>CGST:</span>
                <span>
                  ₹
                  {updatedCart
                    .reduce(
                      (sum, item) => sum + (item.taxBreakup?.cgst || 0),
                      0
                    )
                    .toFixed(2)}
                </span>
              </div>
              <div className='flex justify-between'>
                <span>SGST:</span>
                <span>
                  ₹
                  {updatedCart
                    .reduce(
                      (sum, item) => sum + (item.taxBreakup?.sgst || 0),
                      0
                    )
                    .toFixed(2)}
                </span>
              </div>
              <div className='flex justify-between'>
                <span>IGST:</span>
                <span>
                  ₹
                  {updatedCart
                    .reduce(
                      (sum, item) => sum + (item.taxBreakup?.igst || 0),
                      0
                    )
                    .toFixed(2)}
                </span>
              </div>
              {updatedCart.some((item) => (item.taxBreakup?.cess || 0) > 0) && (
                <div className='flex justify-between'>
                  <span>CESS:</span>
                  <span>
                    ₹
                    {updatedCart
                      .reduce(
                        (sum, item) => sum + (item.taxBreakup?.cess || 0),
                        0
                      )
                      .toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-blue-950/40 border border-blue-800/40' : 'bg-blue-50'}`}>
          <h4 className='text-sm font-semibold text-blue-700 mb-1'
              style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
            {t("CARTEXTRA.DELIVERY_INFORMATION")}
          </h4>
          <div className='text-xs space-y-1'
               style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
            {deliveryMethod === "home_delivery" ? (
              <>
                <p>Delivery to: {deliveryAddress || "Address not set"}</p>
                {deliveryCity && <p>{t("PROFILE.BUSINESS_CITY")}: {deliveryCity}</p>}
                {deliveryState && <p>{t("FORM.SATAE")}: {deliveryState}</p>}
                {deliveryPincode && <p>{t("COMMON.PINCODE")}: {deliveryPincode}</p>}
                {formatPreferredDeliverySlot() && <p>Preferred delivery slot: {formatPreferredDeliverySlot()}</p>}
              </>
            ) : (
              <p>{t("ORDER_HISTORY.STORE_PICKUP")}: {t("CARTEXTRA.FREE_PICKUP_FROM_STORE")}</p>
            )}
          </div>
        </div>

      </div>
    );
  };

  const renderDeliveryInfo = () => {
    if (checkoutStep === "payment") {
      return (
        <div className={`p-5 border-b ${isDarkMode ? 'bg-blue-950/40 border-blue-900' : 'bg-blue-50 border-blue-100'}`}
             style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
          <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
            <MdBrightness1 className={`text-xs animate-pulse ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} />
            {t("CART_MODEL.ORDER_SUMMARY")}
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
            <div>
              <span className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
               {t("CART_MODEL.DELIVERY_METHOD")}{" "}
              </span>
              <span className={isDarkMode ? 'text-blue-200' : 'text-blue-800'}>
                {deliveryMethod === "home_delivery"
                  ? t("CART_MODEL.HOME_DELIVERY")
                  : t("CART_MODEL.STORE_PICKUP")}
              </span>
            </div>

            {deliveryMethod === "home_delivery" && deliveryAddress && (
              <div>
                <span className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>{t("SIDER.DELIVERY_ADDRESS")}: </span>
                <span className={isDarkMode ? 'text-blue-200' : 'text-blue-800'}>{deliveryAddress}</span>
              </div>
            )}

            {deliveryMethod === "home_delivery" && formatPreferredDeliverySlot() && (
              <div>
                <span className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Preferred delivery slot: </span>
                <span className={isDarkMode ? 'text-blue-200' : 'text-blue-800'}>{formatPreferredDeliverySlot()}</span>
              </div>
            )}

            <div>
              <span className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
               {t("CART_MODEL.PAYMENT_METHOD")}{" "}
              </span>
              <span className={isDarkMode ? 'text-blue-200' : 'text-blue-800'}>
                {paymentMethod === "cod"
                  ? "Cash on Delivery"
                  : paymentMethod === "razorpay"
                    ? "Razorpay (Online)"
                    : "Cashfree (Online)"}
              </span>
            </div>

            <div>
              <span className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                {t("CARTEXTRA.SHIPPING")}{" "}
              </span>
              <span className={`capitalize ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                {shippingCost === 0 ? t("CARTEXTRA.FREE_SHIPPING") : `₹${shippingCost.toFixed(2)}`}
              </span>
            </div>
          </div>

          {paymentMethod !== "cod" && (
            <div className='mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg'>
              <p className='text-yellow-800 text-sm flex items-center gap-2'
                 style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
                <MdPayment className='text-yellow-600' />
                          <strong>{t("CART_MODEL.NOTE")}</strong> {t("CART_MODEL.REDIRECTED_TO_SECURE_GATEWAY")}

              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const renderCheckoutSteps = () => (
    <div className={`${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} rounded-xl shadow-sm p-5 mb-8`}
         style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
      <h2 className='text-xl font-bold mb-4'
          style={{ color: headingStyle.color }}>
       {t("CART_MODEL.CHECKOUT_PROCESS")}
      </h2>
      <div className='flex flex-col space-y-6'>
        <div className='flex items-start'>
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 ${checkoutStep === "cart"
              ? "bg-blue-600 text-white"
              : checkoutStep === "payment" || checkoutStep === "confirmation"
                ? "bg-green-500 text-white"
                : "bg-gray-300 text-gray-700"
              }`}
            style={{
              backgroundColor: checkoutStep === "cart" ? storeTheme?.primaryColor || '#3b82f6' : 
                             checkoutStep === "payment" || checkoutStep === "confirmation" ? '#10b981' : '#d1d5db'
            }}
          >
            {checkoutStep === "confirmation" ? <MdCheck size={20} /> : "1"}
          </div>
          <div className='flex-1'>
            <h3
              className={`font-semibold ${checkoutStep === "cart"
                ? "text-blue-600"
                : checkoutStep === "payment" ||
                  checkoutStep === "confirmation"
                  ? "text-green-600"
                  : "text-gray-600"
                }`}
              style={{
                color: checkoutStep === "cart" ? storeTheme?.primaryColor || '#3b82f6' : 
                       checkoutStep === "payment" || checkoutStep === "confirmation" ? '#10b981' : '#6b7280'
              }}
            >
              {t("CART_MODEL.CART_REVIEW")}
            </h3>
            <p className='text-sm text-gray-600 mt-1'
               style={{ color: storeTheme?.textColor || '#6b7280' }}>
              {t("CARTEXTRA.REVIEW_YOUR_ITEM_AND_QUANTITIES")}
            </p>
          </div>
        </div>

        <div className='flex justify-center'>
          <div className='w-0.5 h-8 bg-gray-300 ml-4'></div>
        </div>

        <div className='flex items-start'>
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 ${checkoutStep === "payment"
              ? "bg-blue-600 text-white"
              : checkoutStep === "confirmation"
                ? "bg-green-500 text-white"
                : "bg-gray-300 text-gray-700"
              }`}
            style={{
              backgroundColor: checkoutStep === "payment" ? storeTheme?.primaryColor || '#3b82f6' : 
                             checkoutStep === "confirmation" ? '#10b981' : '#d1d5db'
            }}
          >
            {checkoutStep === "confirmation" ? <MdCheck size={20} /> : "2"}
          </div>
          <div className='flex-1'>
            <h3
              className={`font-semibold ${checkoutStep === "payment"
                ? "text-blue-600"
                : checkoutStep === "confirmation"
                  ? "text-green-600"
                  : "text-gray-600"
                }`}
              style={{
                color: checkoutStep === "payment" ? storeTheme?.primaryColor || '#3b82f6' : 
                       checkoutStep === "confirmation" ? '#10b981' : '#6b7280'
              }}
            >
              {t("CART_MODEL.PAYMENT_&_DELIVERY")}
            </h3>
            <p className='text-sm text-gray-600 mt-1'
               style={{ color: storeTheme?.textColor || '#6b7280' }}>
            {t("CART_MODEL.CHOOSE_DELIVERY_METHOD_AND_PAYMENT")}
            </p>
          </div>
        </div>

        <div className='flex justify-center'>
          <div className='w-0.5 h-8 bg-gray-300 ml-4'></div>
        </div>

        <div className='flex items-start'>
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 ${checkoutStep === "confirmation"
              ? "bg-green-500 text-white"
              : "bg-gray-300 text-gray-700"
              }`}
            style={{
              backgroundColor: checkoutStep === "confirmation" ? '#10b981' : '#d1d5db'
            }}
          >
            3
          </div>
          <div className='flex-1'>
            <h3
              className={`font-semibold ${checkoutStep === "confirmation"
                ? "text-green-600"
                : "text-gray-600"
                }`}
              style={{
                color: checkoutStep === "confirmation" ? '#10b981' : '#6b7280'
              }}
            >
              {t("CART_MODEL.CONFIRMATION")}
            </h3>
            <p className='text-sm text-gray-600 mt-1'
               style={{ color: storeTheme?.textColor || '#6b7280' }}>
                 {t("CART_MODEL.REVIEW_AND_CONFIRM_YOUR_ORDER")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!storeTheme) {
    return (
      <div className='min-h-screen flex items-center justify-center' style={{ backgroundColor: isDarkMode ? '#0b1220' : '#ffffff' }}>
        <div className='text-center'>
          <div className='w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4'
               style={{ borderColor: '#3b82f6' }}></div>
          <p style={{ color: isDarkMode ? '#e5e7eb' : '#1f2937' }}>Loading cart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex flex-col' style={themeStyles}>
      {/* Toast Notifications */}
      {/* Add Toast component here if needed */}

      {/* NAVBAR */}
      <div className={`${isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-white'} shadow-sm border-b`}>
        <div className='max-w-6xl mx-auto px-4 py-5 flex items-center'>
          <button
            onClick={() => {
              if (checkoutStep === "payment") {
                setCheckoutStep("cart");
              } else {
              handleBackNavigation();
              }
            }}
            className='flex items-center hover:text-blue-800 mr-4 transition-colors'
            style={{ color: storeTheme?.primaryColor || '#3b82f6', fontFamily: storeTheme?.fontFamily || 'Inter' }}
          >
            <MdArrowBack className='text-xl mr-1' />
            {checkoutStep === "payment" ? "Back to Cart" : "Back to Store"}
          </button>
          <div className='flex items-center gap-3'>
      
            <h1 className='text-2xl font-bold'
                style={{ color: headingStyle.color, fontFamily: storeTheme?.fontFamily || 'Inter' }}>
              {checkoutStep === "payment"
                ? t("CART_MODEL.CONFIRM_ORDER")
                : t("CART_MODEL.YOUR_SHOPPING_CART")}
            </h1>
          </div>
        </div>
      </div>

      <div className='max-w-6xl mx-auto px-4 py-8'>
        {cart.length === 0 ? (
          <div className={`${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} rounded-2xl shadow-sm p-10 text-center`}
               style={{ fontFamily: storeTheme?.fontFamily || 'Inter' }}>
            <div className='flex flex-col items-center justify-center'>
              <div className='w-32 h-32 rounded-full flex items-center justify-center mb-6'
                   style={{ backgroundColor: `${storeTheme?.primaryColor || '#3b82f6'}10` }}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-20 w-20'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  style={{ color: storeTheme?.primaryColor || '#3b82f6' }}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
                  />
                </svg>
              </div>
              <p className='text-lg mb-4'
                 style={{ color: storeTheme?.textColor || '#1f2937' }}>
                {t("CART_MODEL.YOUR_CART_IS_EMPTY")}
              </p>
              <button
                onClick={() =>
                  navigate(
                    getStorefrontPath({
                      businessName: displayBusinessName,
                      bussinessUuid,
                    }),
                  )
                }
                className='px-8 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-md'
                style={buttonStyle}
              >
                {t("CART_MODEL.CONTINUE_SHOPPING")}
              </button>
            </div>
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            <div className='lg:col-span-1'>{renderCheckoutSteps()}</div>

            <div className='lg:col-span-2'>
              <div className={`${isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'} rounded-xl shadow-md overflow-hidden`}>
                {renderDeliveryInfo()}
                <div className={`p-5 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                  <h2 className='text-xl font-bold'
                      style={{ color: headingStyle.color, fontFamily: storeTheme?.fontFamily || 'Inter' }}>
                    {checkoutStep === "payment"
                      ? t("CART_MODEL.ORDER_SUMMARY")
                      : t("CART_MODEL.CART_ITEMS")}
                  </h2>
                </div>
                {renderCartItems()}
                {renderOrderSummary()}
                {/* MODIFIED: Don't render shipping info */}
                {/* {renderShippingInfo()} */}

                {/* Checkout Button */}
                {checkoutStep === "cart" && (
                  <div className={`p-6 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                    <button
                      onClick={handleProceedToCheckout}
                      className='w-full py-3 rounded-lg font-semibold hover:scale-[1.02] transition-all duration-300 shadow-md'
                      style={buttonStyle}
                    >
                      {t("CART_MODEL.PROCEED_TO_CHECKOUT")}
                    </button>
                  </div>
                )}

                {checkoutStep === "payment" && (
                  <div
                    ref={confirmOrderRef}
                    className={`p-6 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}
                  >
                    {paymentMethod === "cod" ? (
                      <button
                        onClick={handleConfirmOrder}
                        disabled={isPlacingOrder}
                        className='w-full py-3 rounded-lg font-semibold hover:scale-[1.02] transition-all duration-300 shadow-md disabled:cursor-not-allowed disabled:opacity-70'
                        style={buttonStyle}
                      >
                        {isPlacingOrder ? (
                          <span className='inline-flex items-center justify-center gap-2'>
                            <span className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                            Placing order...
                          </span>
                        ) : (
                          <>Confirm COD Order - ₹{calculateOrderTotals().grandTotal}</>
                        )}
                      </button>
                    ) : paymentMethod === "razorpay" ? (
                      <button
                        onClick={handleRazorpayPayment}
                        disabled={isPlacingOrder}
                        className='w-full py-3 rounded-lg font-semibold hover:scale-[1.02] transition-all duration-300 shadow-md disabled:cursor-not-allowed disabled:opacity-70'
                        style={buttonStyle}
                      >
                        {isPlacingOrder ? (
                          <span className='inline-flex items-center justify-center gap-2'>
                            <span className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                            Processing order...
                          </span>
                        ) : (
                          <>Pay with Razorpay - ₹{calculateOrderTotals().grandTotal}</>
                        )}
                      </button>
                    ) : paymentMethod === "cashfree" ? (
                      <button
                        onClick={handleCashfreePayment}
                        disabled={isPlacingOrder}
                        className='w-full py-3 rounded-lg font-semibold hover:scale-[1.02] transition-all duration-300 shadow-md disabled:cursor-not-allowed disabled:opacity-70'
                        style={buttonStyle}
                      >
                        {isPlacingOrder ? (
                          <span className='inline-flex items-center justify-center gap-2'>
                            <span className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                            Processing order...
                          </span>
                        ) : (
                          <>Pay with Cashfree - ₹{calculateOrderTotals().grandTotal}</>
                        )}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dialogs */}
        <AccountLoginDialog
          open={accountDialogOpen}
          onClose={() => setAccountDialogOpen(false)}
          onLogin={handleAccountLogin}
          onGuestMode={handleGuestMode}
          theme={storeTheme}
        />

        <DeliveryMethodDialog
          open={deliveryMethodDialogOpen}
          onClose={() => setDeliveryMethodDialogOpen(false)}
          onSelect={handleDeliveryMethodSelect}
          selectedMethod={deliveryMethod}
          theme={storeTheme}
        />

        <AddressSelectionDialog
          open={addressSelectionDialogOpen}
          onClose={() => setAddressSelectionDialogOpen(false)}
          addresses={userAddresses}
          onSelect={handleAddressSelect}
          onAddNew={handleAddNewAddress}
          theme={storeTheme}
        />

        <AddressFormDialog
          open={addressDialogOpen}
          onClose={() => setAddressDialogOpen(false)}
          onSubmit={handleAddressSubmit}
          initialAddress={userProfile?.address || ""}
          isEditing={!!userProfile?.address}
          theme={storeTheme}
          isDeliveryPincodeSupported={isDeliveryPincodeSupported}
        />

        {/* MODIFIED: Comment out ShippingMethodDialog */}
        {/* 
        <ShippingMethodDialog
          open={shippingMethodDialogOpen}
          onClose={() => setShippingMethodDialogOpen(false)}
          onSelect={handleShippingMethodSelect}
          selectedMethod={shippingMethod}
          shippingConfig={shippingConfig}
          loading={loadingCalculation}
          shippingCost={shippingCost}
          deliveryState={deliveryState}
          storeState={store?.state || ""}
          deliveryCity={deliveryCity}
          storeCity={store?.city || ""}
          orderCalculation={orderCalculation}
          orderValue={orderValue}
          orderWeight={orderWeight}
          theme={storeTheme}
        />
        */}
<RiskOtpDialog
  open={riskOtpDialogOpen}
  onClose={() => {
    console.log("Closing OTP dialog");
    setRiskOtpDialogOpen(false);
    setRiskOtpStatus("pending");
    setRiskOtp("");
    setDevOtp(null);
    if (pendingOrderCallback) {
      pendingOrderCallback(false);
      setPendingOrderCallback(null);
    }
  }}
  onVerify={(otpValue) => {
    console.log("Verify button clicked with OTP:", otpValue);
    return verifyRiskOtpCode(otpValue);
  }}
  onResend={() => {
    console.log("Resend button clicked");
    return resendRiskOtpCode();
  }}
  otp={riskOtp}
  setOtp={(value) => {
    console.log("Setting OTP to:", value);
    setRiskOtp(value);
  }}
  loading={riskOtpLoading}
  resendDisabled={riskOtpResendDisabled}
  countdown={riskOtpCountdown}
  otpStatus={riskOtpStatus}
  phoneNumber={verificationPhone || userProfile?.phone || userProfile?.customer_mobile || ""}
  devOtp={devOtp}
  theme={storeTheme}
/>
     <PaymentMethodDialog
  open={paymentDialogOpen}
  total={calculateOrderTotals().subtotal}
  shippingCost={shippingCost}
  taxAmount={calculateOrderTotals().taxAmount}
  grandTotal={calculateOrderTotals().grandTotal}
  onClose={handleClosePaymentMethod}
  couponDiscount={couponDiscount}
  appliedCoupon={appliedCoupon}
  onPay={handlePay}
  deliveryMethod={deliveryMethod}
  address={deliveryAddress}
  businessUuid={bussinessUuid}
  deliverySlotEnabled={deliverySlotEnabled === true}
  preferredDeliveryDate={preferredDeliveryDate}
  preferredDeliveryTime={preferredDeliveryTime}
  onPreferredDeliveryDateChange={setPreferredDeliveryDate}
  onPreferredDeliveryTimeChange={setPreferredDeliveryTime}
  cashOnDeliveryEnabled={cashOnDeliveryEnabled} 
  theme={storeTheme}
/>
{/* OTP Verification Dialog */}
<OTPVerificationDialog
  open={otpDialogOpen}
  onClose={() => {
    setOtpDialogOpen(false);
    if (pendingOTPVerification) {
      pendingOTPVerification(false);
      setPendingOTPVerification(null);
    }
  }}
  onVerify={(verified) => {
    if (pendingOTPVerification) {
      pendingOTPVerification(verified);
      setPendingOTPVerification(null);
    }
  }}
  phone={verificationPhone}
  theme={storeTheme}
/>

{/* Risk Assessment Details Dialog */}
<RiskAssessmentDialog
  open={riskAssessmentDetails.show}
  onClose={() => setRiskAssessmentDetails({ ...riskAssessmentDetails, show: false })}
  assessment={riskAssessmentDetails}
  theme={storeTheme}
/>
        <OrderConfirmationDialog
          open={orderConfirmationDialogOpen}
          onClose={handleOrderConfirmationClose}
          orderId={orderId}
          shippingCost={shippingCost}
          taxAmount={taxAmount}
          grandTotal={grandTotal}
          theme={storeTheme}
        />
      </div>
    </div>
  );
}

export default CartPage;